import RecordedPreview from './RecordedPreview';
import { FullscreenVideo } from './FullscreenVideo';
import MessageStatusIcon from './MessageStatusIcon';
import OutboxMessages from './OutboxMessages';
import NotificationIcon from './NotificationIcon';
import SupportNotificationSettings from './SupportNotificationSettings';
import { canManageSupportNotifications } from '../lib/supportNotifications';
import { messageStatus } from '../lib/messageStatus';
import { isReplySwipe, voiceHoldAction, VoiceHold } from '../lib/chatGestures';
import { Fragment, lazy, memo, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SearchResultsPanel from './SearchResultsPanel';
import i18n from '../i18n';
import { useHeaderActions } from '../lib/useHeaderActions';
import { getDraft, setDraft } from '../lib/drafts';
import { scrollDownTarget, unreadMessages } from '../lib/scrollToUnread';
import { loadEmptyHistoryUntilVisible } from '../lib/emptyHistoryAutoload';
import { createPortal } from 'react-dom';
import { realtime } from '../realtime';
import { getTranscriptCached, imageSrc, makeMediaErrorRetry, thumbnailSrc, transcribe, uploadMedia, uploadMediaWithProgress } from '../api/media';
import { fetchLinkPreview, hasUsefulPreview } from '../api/linkPreview';
import { getPinnedMessages, searchMessages, setChatMute, setMessagePin, setMessageReaction, listMembers, listRooms, type ChatRoomDto } from '../api/chats';
import { isChatMutedAt, type ChatMuteMode } from '../lib/chatMute';
import { getPresence } from '../api/presence';
import { getUserBrief, setDefaultRecordType as apiSetDefaultRecordType } from '../api/user';
import { addContact, dismissAddContactPrompt, shouldPromptAddContact } from '../api/contacts';
import { ApiError, randomUUID, getAuthSession } from '../api/client';
import { formatPresence, PRESENCE_POLL_MS } from '../lib/presence';
import { mergePinnedMessages } from '../lib/pinned';
import { intlLocale } from '../lib/language';
import { useConnectionStatus } from '../lib/useConnectionStatus';
import Avatar from './Avatar';
import RoomAvatar from './RoomAvatar';
import { backdropClose } from '../lib/modalBackdrop';
import ConnectingBadge from './ConnectingBadge';
import { AudioPlayer, DocumentCard, RoundVideoPlayer } from './MediaWidgets';
import { isRoundVideoMsg, useVideoPoster } from '../lib/mediaHelpers';
import ChatInfoModal from './ChatInfoModal';
import UserInfoModal from './UserInfoModal';
import ReactionListModal from './ReactionListModal';
import MessageInfoModal from './MessageInfoModal';
import ErrorBoundary from './ErrorBoundary';
import ConfirmModal from './ConfirmModal';
import Emoji from './Emoji';
import ForwardModal from './ForwardModal';
import ContactPickerModal from './ContactPickerModal';
import { buildContactReferenceMessage } from '../lib/contactReference';
import AttachPreviewModal, { type AttachmentSource, type StagedAttachment } from './AttachPreviewModal';
import { compressPhotoForUpload } from '../lib/compressPhoto';
import { requestTransferAuthorization, showTransferNotice } from '../lib/transferPolicy';
import { albumScope, downloadSequentially, mediaNavigationIndex } from '../lib/albumDownload';
import { copyableMessageText, messageDownloadItems } from '../lib/messageActions';
import { formatMessageTime } from '../lib/messageTime';
import { VOICE_MAX_MS, formatRecordingLimit } from '../lib/recordingLimit';
import { classifyFile } from '../lib/mediaHelpers';
import CloudLimitModal from './CloudLimitModal';
import PinConfirmModal from './PinConfirmModal';
import LinkPreviewCard from './LinkPreviewCard';
import MessageText from './MessageText';
import { backspaceInput } from '../lib/emojiBackspace';
import { isSoloEmoji } from '../lib/emoji';
import { firstUrl } from '../lib/url';
import { senderColor } from '../lib/colors';
import type { ChatSummary, LinkPreviewDto, MediaItem, MessageDto, OpenLightbox, PresenceDto, UserBriefDto, UserDto } from '../api/types';

// Полный эмодзи-пикер (1911 эмодзи, см. emojiCategories.ts) — не нужен, пока пользователь его
// не открыл (обычный текст набирается без него) — выносим в отдельный чанк.
const EmojiPicker = lazy(() => import('./EmojiPicker'));

// Листовые компоненты (MediaImage/VideoPlayer/GalleryThumb) сами уже знают fileId/kind, но не
// messageId/senderId — им передаётся урезанная OpenLightboxSimple, обёрнутая на уровне
// renderMedia/GalleryThumb. Полная сигнатура OpenLightbox — общая с ChatInfoModal, см. api/types.
// startTime — секунда, с которой должно продолжиться воспроизведение в лайтбоксе (см.
// VideoPlayer.onOpen ниже): без него разворот видео из компактного вида всегда перематывал
// на начало, даже если пользователь уже что-то посмотрел в ленте. posterUrl — уже загруженный
// кадр превью (тот же, что показывался до старта воспроизведения) — передаём его в лайтбокс
// как HTML poster, иначе на миг между открытием и декодированием первого кадра виден чёрный
// прямоугольник вместо картинки.
type OpenLightboxSimple = (src: string, kind: 'image' | 'video', startTime?: number, posterUrl?: string) => void;

/**
 * В ленте показываем только миниатюру (256px, /thumbnail) — полноразмерный оригинал
 * подгружается лишь при клике (открытие лайтбокса), не сразу при рендере сообщения. Иначе
 * при открытии чата с полусотней фото-сообщений сразу качались бы полсотни оригиналов разом,
 * даже если пользователь ни одно не разворачивал.
 */
function MediaImage({ fileId, sizeBytes, onOpen }: { fileId?: string; sizeBytes?: number; onOpen: OpenLightboxSimple }) {
  const { t } = useTranslation('chatView');
  const [thumb, setThumb] = useState<string | null>(null);
  const [opening, setOpening] = useState(false);
  useEffect(() => {
    if (!fileId) return;
    // thumbnailSrc кэширует blob-URL по fileId на весь срок жизни вкладки (см. api/media.ts) —
    // URL общий для всех виджетов этого fileId, поэтому здесь его больше НЕ ревокаем.
    let alive = true;
    thumbnailSrc(fileId)
      .then((u) => {
        if (alive) setThumb(u);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [fileId]);

  const handleOpen = async () => {
    if (!fileId || opening) return;
    setOpening(true);
    try {
      const authorization = await requestTransferAuthorization({ direction: 'download', label: t('media.originalPhoto'), sizeBytes: sizeBytes ?? null });
      if (!authorization.allowed) {
        showTransferNotice(authorization.reason ?? t('media.wifiRequired'));
        return;
      }
      const full = await imageSrc(fileId);
      onOpen(full, 'image');
    } catch {
      /* не критично — просто не откроется в этот раз */
    } finally {
      setOpening(false);
    }
  };

  return thumb ? (
    <img src={thumb} alt="" style={{ cursor: opening ? 'wait' : 'zoom-in' }} onClick={handleOpen} />
  ) : (
    <div style={{ color: 'var(--muted)' }}>{t('loading')}</div>
  );
}

/** Распознавание речи (whisper.cpp на сервере, см. TranscriptionService), по fileId. */
export type TranscriptState = { status: 'busy' | 'done' | 'error'; text?: string; error?: string };
type PendingRecordedUpload = {
  id: number;
  blob: Blob;
  name: string;
  mimeType: string;
  kind: 'voice' | 'video';
  status: 'sending' | 'waiting' | 'error';
  uploadedFileId?: string;
  clientMsgId?: string;
  error?: string;
};

/**
 * Результат распознавания под голосовым/аудио-сообщением — только отображение, без своей
 * кнопки: запуск теперь через контекстное меню сообщения (см. пункт "Расшифровать" в ctx-menu),
 * а состояние живёт на уровне ChatView (карта transcripts), а не внутри плеера — иначе текст
 * пропадал бы при перерендере списка сообщений.
 */
const TRANSCRIBE_COLLAPSE_LEN = 240;

function localDateTimeValue(date: Date): string {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

function TranscribeResult({ state, revealed, onHide }: { state?: TranscriptState; revealed?: boolean; onHide?: () => void }) {
  const { t } = useTranslation('chatView');
  // Длинную расшифровку сворачиваем под "Показать полностью" (как в Telegram) — стрелка
  // рядом с кнопкой разворота меняет направление (▾ свёрнуто → ▴ развёрнуто), см. отчёт
  // пользователя "направление стрелки должно меняться".
  const [expanded, setExpanded] = useState(false);
  if (!state || !revealed) return null;
  if (state.status === 'done') {
    const text = state.text ?? '';
    const isLong = text.length > TRANSCRIBE_COLLAPSE_LEN;
    const shown = isLong && !expanded ? text.slice(0, TRANSCRIBE_COLLAPSE_LEN).trimEnd() + '…' : text;
    return (
      <div className="transcribe-text">
        {shown}
        {isLong && (
          <button
            type="button"
            className="transcribe-toggle"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
          >
            {expanded ? t('transcribe.collapse') : t('transcribe.expand')}
            <span className={`transcribe-toggle-arrow${expanded ? ' up' : ''}`}>▾</span>
          </button>
        )}
        <button type="button" className="transcribe-toggle" onClick={(e) => { e.stopPropagation(); onHide?.(); }}>
          {t('transcribe.hide')}
        </button>
      </div>
    );
  }
  if (state.status === 'busy') {
    return (
      <div className="transcribe-row">
        <span className="transcribe-busy">{t('transcribe.busy')}</span>
      </div>
    );
  }
  return (
    <div className="transcribe-row">
      <span className="transcribe-error">{state.error}</span>
    </div>
  );
}

/** Есть ли у сообщения запись, которую можно расшифровать — ТОЛЬКО голосовое (audio без
 *  fileName) и видео-сообщение-кружок (video без fileName). Музыка (audio с fileName) и любые
 *  файлы (даже аудио-файл) расшифровке не подлежат (см. отчёт пользователя "у музыки и файлов
 *  даже если это аудио файл не должно быть расшифровки"). Сообщение может нести несколько
 *  вложений — берём первое подходящее. */
function transcribeFileId(msg: MessageDto): string | undefined {
  const mediaList = msg.payload.data?.media ?? [];
  for (const media of mediaList) {
    if (!media.fileId) continue;
    if ((media.kind === 'audio' || media.kind === 'video') && !media.fileName) return media.fileId;
  }
  return undefined;
}

/**
 * Постер (кадр видео, /thumbnail) — до реального воспроизведения; blob-URL освобождаем при
 * размонтировании/смене fileId, как и у MediaImage. Кадр есть не всегда (старые видео до
 * этой фичи, или ffmpeg недоступен на сервере — см. MediaService.extractVideoFrame) — тогда
 * /thumbnail отвечает 404. "loaded" отдельно от "poster": как только попытка завершилась
 * (успехом или 404), плеер должен стать кликабельным, а не зависать на "Загрузка…" навечно.
 */
/**
 * Пока не нажали play — показываем только постер (кадр видео), сам файл не качаем вообще.
 * Полный signed URL (и тем самым первые байты видео через preload="metadata") запрашивается
 * только по клику — то самое "начинать подгрузку, когда пытаешься просмотреть".
 */
function VideoPlayer({ fileId, messageId, onOpen }: { fileId?: string; messageId: number; onOpen: OpenLightboxSimple }) {
  const { t } = useTranslation('chatView');
  const { poster, loaded } = useVideoPoster(fileId);
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const startPlayback = async () => {
    if (!fileId || src || loading) return;
    setLoading(true);
    try {
      setSrc(await imageSrc(fileId));
    } catch {
      /* не критично — просто не запустится в этот раз */
    } finally {
      setLoading(false);
    }
  };

  // Между кликом и монтированием <video> есть await imageSrc() — цепочка "жеста
  // пользователя" для браузерного autoplay-политики к этому моменту уже разорвана, сам
  // атрибут autoPlay в таких случаях молча не срабатывает (readyState остаётся 0). Явный
  // play() тут же после монтирования срабатывает надёжнее; если браузер всё равно откажет —
  // ничего страшного, элемент просто останется на паузе с уже видимыми native controls.
  useEffect(() => {
    if (src) videoRef.current?.play().catch(() => {});
  }, [src]);

  if (src) {
    return (
      <div className="video-msg-wrap">
        {/* poster — уже загруженный кадр превью (тот же, что показывался до старта
            воспроизведения): без него между монтированием <video> и декодированием первого
            кадра браузер на миг показывает чёрный прямоугольник (см. отчёт пользователя). */}
        {/* data-video-key — по нему лайтбокс при закрытии находит этот же <video> в ленте и
            переставляет позицию воспроизведения на ту, где остановились в развёрнутом виде
            (см. closeLightbox ниже; без этого миниатюра в чате оставалась на старой позиции). */}
        <video
          ref={videoRef}
          controls
          src={src}
          poster={poster ?? undefined}
          className="video-msg"
          preload="metadata"
          onError={makeMediaErrorRetry(fileId, src, setSrc)}
          data-video-key={`${messageId}:${fileId}`}
        />
        {/* Раньше здесь была прозрачная кликзона поверх всего кадра — она перехватывала
            клики и по самому видео (пользователь не мог поставить на паузу тапом/кликом
            по нативным controls, см. отчёт пользователя). Теперь разворот в лайтбокс —
            отдельная маленькая кнопка в углу, не перекрывающая controls, и передаёт
            текущую позицию воспроизведения — иначе лайтбокс всегда начинал сначала. */}
        <button
          type="button"
          className="video-msg-expand"
          title={t('media.expandVideo')}
          onClick={(e) => {
            e.stopPropagation();
            // Иначе этот <video> продолжает играть в ленте одновременно с новым <video> в
            // лайтбоксе (тот же src) — оба независимо декодируют и выводят звук, поток
            // буквально дублируется (см. отчёт пользователя). Лайтбокс подхватывает
            // воспроизведение с той же позиции (currentTime передаётся ниже), так что для
            // пользователя это выглядит как бесшовный переход, а не остановка.
            videoRef.current?.pause();
            onOpen(src, 'video', videoRef.current?.currentTime || 0, poster ?? undefined);
          }}
        >
          <img src="/expand.svg" alt="" />
        </button>
      </div>
    );
  }
  if (!loaded) return <div style={{ color: 'var(--muted)' }}>{t('loading')}</div>;
  // Кадра может не быть (старое видео до этой фичи, ffmpeg недоступен на сервере) — плеер
  // всё равно должен быть кликабельным, просто без картинки-превью под кнопкой play.
  return (
    <div className="video-msg-wrap video-msg-poster" onClick={startPlayback} style={{ cursor: loading ? 'wait' : 'pointer' }}>
      {poster ? <img src={poster} alt="" className="video-msg" /> : <div className="video-msg-poster-blank" />}
      <div className="video-round-play"><img src="/play.svg" alt="" /></div>
    </div>
  );
}

// RoundVideoPlayer и isRoundVideoMsg вынесены в MediaWidgets.tsx — их переиспользует и
// категория «Голосовые» инфопанели (ChatInfoModal), см. отчёт пользователя.

/** Быстрые реакции сверху контекстного меню (правый клик по сообщению) — как в Telegram. */
const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🔥'];

const EMOJI_FX: [string[], string][] = [
  [['😢', '😭', '🥲', '😿', '😥', '🥹', '😪'], 'fx-cry'],
  [['🔥'], 'fx-fire'],
  [['❤', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🤎', '💕', '💖', '💗', '💓', '💞', '😍', '🥰'], 'fx-heart'],
  [['😂', '🤣', '😆'], 'fx-laugh'],
  [['😠', '😡', '🤬'], 'fx-angry'],
  [['✨', '🤩', '🌟', '⭐'], 'fx-sparkle'],
  [['👍', '👏', '🙌', '🎉'], 'fx-clap'],
];

/** Класс анимации-реакции по смыслу эмодзи (плач/огонь/сердце/...), либо '' для общего покачивания. */
function emojiEffectClass(text: string): string {
  const base = text.replace(/[️‍]/g, '').replace(/[\u{1F3FB}-\u{1F3FF}]/gu, '');
  for (const [chars, cls] of EMOJI_FX) {
    if (chars.includes(base)) return cls;
  }
  return '';
}

/** Одно вложение по своему kind — не зависит от msg.type (тот слишком грубый для
 *  сообщений с разнородными вложениями, см. renderMedia). */
function renderSingleMedia(media: MediaItem, onOpen: OpenLightbox, messageId: number, senderId: string): React.ReactNode {
  const simpleOpen: OpenLightboxSimple = (src, kind, startTime, posterUrl) => onOpen(src, kind, messageId, senderId, media.fileId!, startTime, posterUrl);
  if (media.kind === 'audio') return <AudioPlayer fileId={media.fileId} fileName={media.fileName} sizeBytes={media.sizeBytes} showRate={!media.fileName} />;
  if (media.kind === 'video') {
    return !media.fileName
      ? <RoundVideoPlayer fileId={media.fileId} />
      : <VideoPlayer fileId={media.fileId} messageId={messageId} onOpen={simpleOpen} />;
  }
  if (media.kind === 'file') {
    return <DocumentCard fileId={media.fileId} fileName={media.fileName} sizeBytes={media.sizeBytes} />;
  }
  return <MediaImage fileId={media.fileId} sizeBytes={media.sizeBytes} onOpen={simpleOpen} />;
}

const REACTION_PICKER_W = 320;
const REACTION_PICKER_H = 380;

/**
 * Пикер реакций рендерится порталом в document.body (см. ctx-reaction-picker-portal) —
 * иначе будучи вложенным в .ctx-menu (у того overflow:hidden ради скруглённых углов пунктов
 * меню при hover) сетка эмодзи обрезалась бы рамками самого контекстного меню. Позиция —
 * над контекстным меню, если сверху хватает места, иначе под ним.
 */
function reactionPickerStyle(ctxMenu: { x: number; y: number }): React.CSSProperties {
  const openAbove = ctxMenu.y >= REACTION_PICKER_H + 12;
  const top = openAbove
    ? ctxMenu.y - REACTION_PICKER_H - 8
    : Math.min(ctxMenu.y + 40, window.innerHeight - REACTION_PICKER_H - 4);
  const left = Math.max(4, Math.min(ctxMenu.x, window.innerWidth - REACTION_PICKER_W - 4));
  return { left, top: Math.max(4, top) };
}

/** Бейдж запуска расшифровки — за пределами пузыря, у внутреннего нижнего угла (см. отчёт
 *  пользователя: серия демо-артефактов с вариантами размещения/цвета/шрифта). Заливка —
 *  цвет своего пузыря + контрастная обводка (иначе сливается с пузырём, который перекрывает).
 *  Стрелка + буква "Т" рисуются один раз для входящих ("→Т"), у исходящих то же самое зеркалим
 *  через transform: scaleX(-1) на весь бейдж — даёт "Т←" без отдельной вёрстки под каждую
 *  сторону (буква "Т" симметрична, зеркалирование её не меняет, а стрелка разворачивается
 *  как надо). Прячется, когда расшифровка уже идёт/готова — тогда показывается текст/статус.
 *  Пункт "Расшифровать" в контекстном меню при этом остаётся (см. ctx-menu). */
function TranscribeBadge({ state, onRun, onReveal, revealed, mine }: { state?: TranscriptState; onRun: () => void; onReveal?: () => void; revealed?: boolean; mine: boolean }) {
  const { t } = useTranslation('chatView');
  if (state?.status === 'busy') return null;
  if (state?.status === 'done' && !revealed) {
    return (
      <button
        type="button"
        className={'transcribe-badge' + (mine ? ' me' : ' other')}
        title={t('transcribe.show')}
        onClick={(e) => {
          e.stopPropagation();
          onReveal?.();
        }}
      >
        <svg className="transcribe-badge-t" viewBox="0 0 24 24" width="10" height="10">
          <path d="M5 5h14v3.5h-5.25V19h-3.5V8.5H5z" fill="currentColor" />
        </svg>
      </button>
    );
  }
  if (state?.status === 'done') return null;
  return (
    <button
      type="button"
      className={'transcribe-badge' + (mine ? ' me' : ' other')}
      title={t('transcribe.action')}
      onClick={(e) => {
        // Без stopPropagation клик долетает до контейнера ленты и попутно выделяет
        // сообщение (см. onClick на .messages — клик по пустому полю напротив сообщения).
        e.stopPropagation();
        onRun();
      }}
    >
      <svg className="transcribe-badge-arrow" viewBox="0 0 24 24" width="9" height="9">
        <path d="M4 12h13M12 6l7 6-7 6" stroke="currentColor" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {/* Буква "Т" — залитый SVG-path (жирное прямое начертание), а не текстовый символ:
          не зависит от того, есть ли в системе шрифт с нужной насыщенностью, и остаётся
          идеально резкой при зеркалировании исходящего бейджа (transform: scaleX(-1)). */}
      <svg className="transcribe-badge-t" viewBox="0 0 24 24" width="10" height="10">
        <path d="M5 5h14v3.5h-5.25V19h-3.5V8.5H5z" fill="currentColor" />
      </svg>
    </button>
  );
}

function renderMedia(msg: MessageDto, onOpen: OpenLightbox, mine: boolean, transcript?: TranscriptState,
                    onTranscribe?: (id: number) => void, metadata?: React.ReactNode,
                    revealed?: boolean, onToggleTranscript?: () => void): React.ReactNode {
  const mediaList = msg.payload.data?.media ?? [];
  const transcribeId = transcribeFileId(msg);
  const run = onTranscribe ? () => onTranscribe(msg.id) : undefined;
  if (mediaList.length === 1) {
    const m = mediaList[0];
    return (
      <>
        <div className="transcribe-media-row">
          {renderSingleMedia(m, onOpen, msg.id, msg.senderId)}
          {m.fileId === transcribeId && run && <TranscribeBadge state={transcript} onRun={run} onReveal={onToggleTranscript} revealed={revealed} mine={mine} />}
        </div>
        {m.fileId === transcribeId && <TranscribeResult state={transcript} revealed={revealed} onHide={onToggleTranscript} />}
      </>
    );
  }
  if (mediaList.length > 1) {
    // Групповое вложение (перетащили/выбрали несколько файлов разом, см. AttachPreviewModal) —
    // одно сообщение с массивом media, текст — общая подпись под всеми вложениями.
    const caption = msg.payload.data?.text || '';
    return (
      <>
        <div className="media-group">
          {mediaList.map((m, i) => (
            <div
              className={'media-group-item' + (m.kind === 'image' || m.kind === 'video' ? '' : ' full')}
              key={m.fileId + ':' + i}
              data-file-id={m.fileId}
            >
              <div className="transcribe-media-row">
                {renderSingleMedia(m, onOpen, msg.id, msg.senderId)}
                {m.fileId === transcribeId && run && <TranscribeBadge state={transcript} onRun={run} onReveal={onToggleTranscript} revealed={revealed} mine={mine} />}
              </div>
              {m.fileId === transcribeId && <TranscribeResult state={transcript} revealed={revealed} onHide={onToggleTranscript} />}
            </div>
          ))}
        </div>
        {caption && (
          <div className="text">
            <MessageText text={caption} entities={msg.payload.data?.entities} />
            {metadata}
          </div>
        )}
      </>
    );
  }
  const text = msg.payload.data?.text || '';
  if (isSoloEmoji(text)) {
    const fx = emojiEffectClass(text);
    return (
      <div className={'text msg-emoji-solo' + (fx ? ' ' + fx : '')}>
        <Emoji
          char={text.trim()}
          animated
        />
      </div>
    );
  }
  const preview = msg.payload.data?.linkPreview;
  return (
    <>
      <div className="text">
        <MessageText text={text} entities={msg.payload.data?.entities} />
        {metadata}
      </div>
      {preview && <LinkPreviewCard preview={preview} />}
    </>
  );
}

function mediaLabel(m: MediaItem): string {
  if (m.kind === 'audio') return i18n.t(m.fileName ? 'chatView:copy.audio' : 'chatView:copy.voice');
  if (m.kind === 'video') return i18n.t('chatView:copy.video');
  if (m.kind === 'file') return i18n.t('chatView:copy.file');
  return i18n.t('chatView:copy.image');
}

/** Текст сообщения для копирования в буфер (медиа — плейсхолдер вида "[Видео]"). */
function messageCopyText(msg: MessageDto): string {
  if (msg.type === 99) {
    const d = msg.payload.data as { text?: string } | null;
    return d?.text || '';
  }
  const mediaList = msg.payload.data?.media ?? [];
  if (mediaList.length === 1) return mediaLabel(mediaList[0]);
  if (mediaList.length > 1) {
    const caption = msg.payload.data?.text;
    return i18n.t('chatView:copy.attachments', { count: mediaList.length }) + (caption ? ' ' + caption : '');
  }
  return msg.payload.data?.text || '';
}

/** Фрагмент текста с найденной подстрокой, обёрнутой в <mark> (для панели поиска). */
function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="search-match">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

/** Разделитель дат: «Сегодня» / «Вчера» / «ДД.ММ.ГГГГ». */
function dateLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const msgDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (msgDay.getTime() === today.getTime()) return i18n.t('chatView:date.today');
  if (msgDay.getTime() === yesterday.getTime()) return i18n.t('chatView:date.yesterday');
  return d.toLocaleDateString(intlLocale(i18n.resolvedLanguage ?? i18n.language), { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export const MessageBubble = memo(function MessageBubble({
  msg,
  me,
  statuses,
  onOpen,
  highlight,
  onContextMenu,
  showSender,
  isGroup,
  repliedMsg,
  onJumpToReply,
  selectMode,
  selected,
  ctxTarget,
  onToggleSelect,
  onReply,
  onReact,
  transcript,
  onTranscribe,
  transcriptRevealed,
  onRevealTranscript,
  forwardedFromLive,
}: {
  msg: MessageDto;
  me: UserDto | null;
  statuses: Map<number, string>;
  onOpen: OpenLightbox;
  highlight: boolean;
  onContextMenu: (id: number, x: number, y: number, fileId?: string) => void;
  showSender: boolean;
  isGroup: boolean;
  repliedMsg?: MessageDto | null;
  onJumpToReply: (id: number) => void;
  selectMode: boolean;
  selected: boolean;
  /** Запуск расшифровки голосового — из кнопки-овала рядом с плеером (см. renderMedia). */
  onTranscribe: (id: number) => void;
  /** Сообщение, для которого сейчас открыто контекстное меню — на мобильных подсвечиваем
   *  его рамкой (см. .msg.ctx-target), чтобы было явно видно, к чему относится меню при
   *  долгом нажатии (на десктопе не нужно — там курсор и так на сообщении). */
  ctxTarget?: boolean;
  onToggleSelect: (id: number) => void;
  onReply: (id: number) => void;
  onReact: (id: number, emoji: string) => void;
  transcript?: TranscriptState;
  transcriptRevealed?: boolean;
  onRevealTranscript?: () => void;
  /** Живое текущее имя автора пересылки (по forwardedFromUserId), если успели подтянуть —
   *  иначе снэпшот-строка из payload.data.forwardedFrom (имя на момент пересылки). */
  forwardedFromLive?: string;
}) {
  const swipeStart = useRef<{ x: number; y: number; pointerId: number } | null>(null);
  const suppressSwipeClick = useRef(false);
  // Служебное сообщение (звонки и т.п.) — центрированная строка
  if (msg.type === 99) {
    const d = msg.payload.data as { text?: string; call?: string; missed?: boolean } | null;
    const raw = d?.text || '';
    // Для старых сообщений: эмодзи 📞/🎥 в тексте заменяем на SVG-иконку
    const hasVideoEmoji = raw.includes('🎥');
    const hasCallEmoji = raw.includes('📞');
    const kind = d?.call ?? (hasVideoEmoji ? 'video' : hasCallEmoji ? 'audio' : undefined);
    const cleanText = raw.replace(/[📞🎥]/gu, '').replace(/\s+/g, ' ').trim();
    return (
      <div className="service-msg" id={'msg-' + msg.id}>
        {kind && (
          <img
            src={kind === 'video' ? '/video-call.svg' : '/audio-call.svg'}
            alt=""
            className="service-call-icon"
          />
        )}
        <span>{cleanText || raw}</span>
      </div>
    );
  }
  const mine = msg.senderId === me?.id;
  const time = formatMessageTime(msg.createdAt);
  const status = messageStatus(msg.id, statuses.get(msg.id));
  const soloEmoji = msg.type === 1 && isSoloEmoji(msg.payload.data?.text || '');
  const roundVideo = isRoundVideoMsg(msg);
  const forwardedFrom = forwardedFromLive ?? msg.payload.data?.forwardedFrom;
  const metadata = (
    <span className="time">
      {msg.editedAt ? '✎ ' : ''}{time}
      {mine && <span aria-label={i18n.t('chatView:status.' + status)} className={'msg-ticks' + (status === 'read' ? ' read' : '')}>
        <MessageStatusIcon status={status} />
      </span>}
    </span>
  );
  const inlineMetadata = !soloEmoji && !msg.reactions?.length && !msg.payload.data?.linkPreview &&
    (!(msg.payload.data?.media?.length) || (msg.payload.data.media.length > 1 && !!msg.payload.data.text));
  // Невидимая копия резервирует место в последней строке; время прижато к правому краю.
  const metadataSpace = inlineMetadata ? <span className="msg-time-space" aria-hidden="true">{metadata}</span> : undefined;
  const bubble = (
    <div
      id={'msg-' + msg.id}
      className={
        'msg ' + (inlineMetadata ? 'msg-inline-time ' : '') + (mine ? 'me' : 'other') + (highlight ? ' highlight' : '') +
        (soloEmoji ? ' emoji-solo' : '') + (roundVideo ? ' round-video-msg' : '') + (selected ? ' selected' : '') +
        (ctxTarget ? ' ctx-target' : '')
      }
      style={{ touchAction: 'pan-y pinch-zoom' }}
      onPointerDown={(e) => {
        suppressSwipeClick.current = false;
        if (e.pointerType !== 'touch' || selectMode || !e.isPrimary ||
          (e.target as Element).closest('button, a, input, audio, video, [role="button"]')) return;
        swipeStart.current = { x: e.clientX, y: e.clientY, pointerId: e.pointerId };
      }}
      onPointerUp={(e) => {
        const start = swipeStart.current;
        swipeStart.current = null;
        if (!start || start.pointerId !== e.pointerId || selectMode) return;
        if (isReplySwipe(start, { x: e.clientX, y: e.clientY })) {
          suppressSwipeClick.current = true;
          onReply(msg.id);
        }
      }}
      onPointerCancel={() => { swipeStart.current = null; }}
      onClickCapture={(e) => {
        if (suppressSwipeClick.current) {
          e.preventDefault();
          e.stopPropagation();
          suppressSwipeClick.current = false;
        }
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        onContextMenu(msg.id, e.clientX, e.clientY, (e.target as Element).closest<HTMLElement>('[data-file-id]')?.dataset.fileId);
      }}
      onClick={(e) => {
        // Обычный клик не включает выделение; вход — через явное действие.
        e.stopPropagation();
      }}
    >
      {forwardedFrom && (
        <div className="msg-forwarded">
          <img src="/forward.svg" alt="" /> {i18n.t('chatView:forwardedFrom', { name: forwardedFrom })}
        </div>
      )}
      {showSender && !mine && msg.senderName && <div className="msg-sender" style={{ '--sc': senderColor(msg.senderId) } as React.CSSProperties}>{msg.senderName}</div>}
      {msg.replyToId != null && (
        <div
          className="msg-reply-quote"
          onClick={(e) => {
            e.stopPropagation();
            onJumpToReply(msg.replyToId!);
          }}
        >
          <div className="msg-reply-name" style={repliedMsg && repliedMsg.senderId !== me?.id ? ({ '--sc': senderColor(repliedMsg.senderId) } as React.CSSProperties) : undefined}>
            {repliedMsg ? (repliedMsg.senderId === me?.id ? i18n.t('chatView:reply.self') : repliedMsg.senderName || i18n.t('chatView:reply.fallback')) : i18n.t('chatView:reply.fallback')}
          </div>
          <div className="msg-reply-text">{repliedMsg ? messageCopyText(repliedMsg) : '…'}</div>
        </div>
      )}
      {renderMedia(msg, onOpen, mine, transcript, onTranscribe, metadataSpace, transcriptRevealed, onRevealTranscript)}
      {msg.reactions && msg.reactions.length > 0 && (
        <div className="msg-reactions">
          {msg.reactions.map((r) => (
            <button
              key={r.emoji}
              type="button"
              className={'reaction-pill' + (me && r.userIds.includes(me.id) ? ' mine' : '')}
              onClick={(e) => {
                e.stopPropagation();
                onReact(msg.id, r.emoji);
              }}
            >
              <Emoji char={r.emoji} />
              <span>{r.userIds.length}</span>
            </button>
          ))}
        </div>
      )}
      {metadata}
    </div>
  );
  const content = isGroup && !mine ? (
    <div className="msg-row other">
      <div className="msg-row-avatar">
        <Avatar userId={msg.senderId} name={msg.senderName || '?'} size={28} />
      </div>
      {bubble}
    </div>
  ) : bubble;
  if (!selectMode) return content;
  // В режиме выбора чекбокс — снаружи бабла, справа, а не поверх контента (раньше оверлей
  // перекрывал собой правый верхний угол сообщения). Сам бабл сдвигается влево за счёт
  // justify-content на строке-обёртке — места под чекбокс освобождается, а не откусывается.
  return (
    <div className={'msg-select-row' + (mine ? ' me' : ' other')}>
      {content}
      <div
        className="msg-select-check-wrap"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggleSelect(msg.id);
        }}
      >
        <span className={'msg-select-check' + (selected ? ' checked' : '')}>{selected ? '✓' : ''}</span>
      </div>
    </div>
  );
});

interface Props {
  chatId: string;
  chat: ChatSummary | null;
  chats: ChatSummary[];
  messages: MessageDto[];
  historyLoaded: boolean;
  me: UserDto | null;
  onLoadOlder: (chatId: string, roomId?: string | null) => Promise<'visible' | 'empty-more' | 'end'>;
  onBack: () => void;
  statuses: Map<number, string>;
  onEditMessage: (chatId: string, messageId: number, newText: string) => Promise<void>;
  onDeleteMessage: (chatId: string, messageId: number) => Promise<void>;
  onStartCall: (video: boolean) => void;
  callActive: boolean;
  onChatsChanged: () => void;
  /** id пользователей, сейчас печатающих в этом чате (WS-событие typing, см. App.tsx). */
  typingUserIds: string[];
  /** Переключиться на другой чат (вкладка "Группы" инфопанели — переход в общую группу). */
  onOpenChat: (chatId: string) => void;
  /** Активная комната группового чата (null — общая), см. App.switchRoom. Переключение
   *  теперь в RoomsColumn (см. App.tsx) — тут только для догрузки истории/имени в шапке. */
  roomId?: string | null;
  /** Сообщение, к которому нужно проскроллить и подсветить при открытии (переход из
   *  глобального поиска, см. App.tsx/GlobalSearchResults) — best-effort: если сообщение ещё
   *  не подгружено в DOM, попытка тихо не удаётся, как и у обычного goToMessage ниже. */
  jumpToMessageId?: number | null;
  onJumpHandled?: () => void;
  /** Сколько сообщений было непрочитано НА МОМЕНТ открытия этого чата (см. App.tsx openChat) —
   *  используется только для начального скролла (первое непрочитанное вместо самого конца,
   *  см. отчёт пользователя "если прочитано до какого-то сообщения, чат должен открываться
   *  на этом же месте"), дальнейшие изменения не отслеживаются. */
  unreadOnOpen?: number;
  unreadCount?: number;
  onVisibleRead?: (messageId: number) => void;
}

export default function ChatView({
  chatId,
  chat,
  chats,
  messages,
  historyLoaded,
  me,
  onLoadOlder,
  onBack,
  statuses,
  onEditMessage,
  onDeleteMessage,
  onStartCall,
  callActive,
  onChatsChanged,
  typingUserIds,
  onOpenChat,
  roomId = null,
  jumpToMessageId = null,
  onJumpHandled,
  unreadOnOpen = 0,
  unreadCount = 0,
  onVisibleRead,
}: Props) {
  const { t } = useTranslation('chatView');
  // Захватываем вход до любых await, включая загрузку вложений.
  const sendSession = getAuthSession();
  const [text, setText] = useState('');
  // Комнаты (темы) группового чата — переключение и управление ими теперь в отдельной колонке
  // (см. RoomsColumn/App.tsx, отчёт пользователя "комнаты в групповых чатах надо переделать").
  // Здесь остался только лёгкий lookup текущей комнаты — для заголовка шапки чата (имя
  // "Апти · Звонки" и её же аватар, см. отчёт "в заголовке не отображается иконка комнаты"),
  // без дублирования состояния/управления из RoomsColumn.
  const [activeRoom, setActiveRoom] = useState<ChatRoomDto | null>(null);
  useEffect(() => {
    setActiveRoom(null);
    if (!chat?.hasRooms || !roomId) return;
    let alive = true;
    listRooms(chatId).then((list) => {
      if (!alive) return;
      setActiveRoom(list.find((r) => r.id === roomId) ?? null);
    }).catch(() => {});
    return () => {
      alive = false;
    };
  }, [chatId, chat?.hasRooms, roomId]);
  const roomName = activeRoom?.name ?? null;
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [attachMenuOpen, setAttachMenuOpen] = useState(false);
  const [contactPickerOpen, setContactPickerOpen] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const messagesInnerRef = useRef<HTMLDivElement>(null);
  const loadingOlderRef = useRef(false);
  // Читается синхронно из handleScroll ПОСЛЕ await onLoadOlder, чтобы понять, не переключился
  // ли пользователь на другой чат за время запроса (см. ниже) — обычная переменная chatId
  // в замыкании handleScroll для этого не годится, она фиксируется на момент вызова.
  const chatIdRef = useRef(chatId);
  chatIdRef.current = chatId;
  const taRef = useRef<HTMLTextAreaElement>(null);
  const typingActiveRef = useRef(false);
  const typingStopTimerRef = useRef<number | null>(null);
  const [lightbox, setLightbox] = useState<
    { src: string; kind: 'image' | 'video'; messageId: number; senderId: string; fileId: string; startTime?: number; posterUrl?: string } | null
  >(null);
  const [lightboxMenuOpen, setLightboxMenuOpen] = useState(false);
  const openLightbox: OpenLightbox = useCallback((src, kind, messageId, senderId, fileId, startTime, posterUrl) => {
    setLightboxMenuOpen(false);
    setLightbox({ src, kind, messageId, senderId, fileId, startTime, posterUrl });
  }, []);
  // Управление остаётся видимым во время воспроизведения и поворота экрана (Q-068).
  // Текущая позиция воспроизведения видео в лайтбоксе — при закрытии переносим её обратно на
  // тот же <video> в ленте (см. VideoPlayer.data-video-key), иначе миниатюра оставалась на
  // прежней позиции, хотя в развёрнутом виде пользователь мог перемотать далеко вперёд
  // (см. отчёт пользователя).
  const lightboxTimeRef = useRef(0);
  useEffect(() => {
    lightboxTimeRef.current = lightbox?.startTime ?? 0;
  }, [lightbox?.fileId, lightbox?.messageId, lightbox?.startTime]);
  const closeLightbox = () => {
    if (lightbox?.kind === 'video') {
      const key = `${lightbox.messageId}:${lightbox.fileId}`;
      const el = document.querySelector<HTMLVideoElement>(`video[data-video-key="${CSS.escape(key)}"]`);
      if (el) el.currentTime = lightboxTimeRef.current;
    }
    setLightbox(null);
  };
  // Все фото/видео загруженных сообщений чата, в порядке ленты — для перелистывания
  // вперёд/назад внутри лайтбокса (см. lightboxNav). Не претендует на полноту истории —
  // только то, что уже подгружено в messages, как и everywhere else в этом компоненте.
  const lightboxAllMedia = useMemo(() => {
    const list: { fileId: string; kind: 'image' | 'video'; messageId: number; senderId: string; sizeBytes?: number; fileName?: string }[] = [];
    for (const m of messages) {
      for (const media of m.payload.data?.media ?? []) {
        if ((media.kind === 'image' || media.kind === 'video') && media.fileId) {
          list.push({ fileId: media.fileId, kind: media.kind, messageId: m.id, senderId: m.senderId, sizeBytes: media.sizeBytes, fileName: media.fileName ?? undefined });
        }
      }
    }
    return list;
  }, [messages]);
  // Сообщение-альбом (2+ фото/видео): листаем и считаем только внутри него, со счётчиком «3/7».
  // Одиночное вложение — прежнее поведение (по всей ленте чата).
  const albumList = useMemo(() => albumScope(lightboxAllMedia, lightbox?.messageId), [lightboxAllMedia, lightbox?.messageId]);
  const isAlbum = albumList.length > 1;
  const lightboxMediaList = isAlbum ? albumList : lightboxAllMedia;
  const lightboxIndex = lightbox
    ? lightboxMediaList.findIndex((x) => x.fileId === lightbox.fileId && x.messageId === lightbox.messageId)
    : -1;
  const lightboxDownloads = messageDownloadItems(
    messages.find((message) => message.id === lightbox?.messageId), lightbox?.fileId,
  );
  const lightboxDownloadCurrent = lightboxDownloads.current ?? lightbox;
  // Альбом останавливается на первом и последнем вложении.
  const lightboxNav = async (delta: number) => {
    if (lightboxIndex < 0 || lightboxMediaList.length === 0) return;
    const index = mediaNavigationIndex(lightboxIndex, lightboxMediaList.length, delta, isAlbum);
    const target = lightboxMediaList[index];
    if (!target) return;
    setLightboxMenuOpen(false);
    try {
      if (target.kind === 'image') {
        const authorization = await requestTransferAuthorization({
          direction: 'download',
          label: t('media.originalPhoto'),
          sizeBytes: target.sizeBytes ?? null,
        });
        if (!authorization.allowed) {
          showTransferNotice(authorization.reason ?? t('media.wifiRequired'));
          return;
        }
      }
      const src = await imageSrc(target.fileId);
      setLightbox({ src, kind: target.kind, messageId: target.messageId, senderId: target.senderId, fileId: target.fileId });
    } catch {
      /* не критично — останемся на текущем */
    }
  };
  // Свайп пальцем в лайтбоксе на телефоне — влево/вправо переключает на следующее/предыдущее
  // (до края альбома, см. lightboxNav). Только touch (e.pointerType — мышью на десктопе drag ничего
  // не переключает, там уже есть стрелки и клик по фону, менять это поведение не просили).
  // swipedRef — иначе клик-закрытие (onClick на .lightbox) сработал бы сразу вслед за свайпом:
  // pointerup от свайпа — это тот же жест, что обычный клик, browser не различает их сам.
  const lightboxSwipeStart = useRef<{ x: number; y: number } | null>(null);
  const lightboxSwiped = useRef(false);
  const LIGHTBOX_SWIPE_THRESHOLD = 60;
  const onLightboxPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType !== 'touch') return;
    lightboxSwipeStart.current = { x: e.clientX, y: e.clientY };
  };
  const onLightboxPointerUp = (e: React.PointerEvent) => {
    const start = lightboxSwipeStart.current;
    lightboxSwipeStart.current = null;
    if (!start || e.pointerType !== 'touch' || lightboxMediaList.length <= 1) return;
    const dx = e.clientX - start.x;
    const dy = e.clientY - start.y;
    if (Math.abs(dx) > LIGHTBOX_SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
      lightboxSwiped.current = true;
      lightboxNav(dx < 0 ? 1 : -1);
    }
  };
  const onLightboxClick = () => {
    if (lightboxSwiped.current) {
      lightboxSwiped.current = false;
      return;
    }
    closeLightbox();
  };
  // Скачивание вложений альбома из просмотрщика: последовательно, общий прогресс, итог
  // «Сохранено N из M»; ошибка одного файла не прерывает остальные. albumSavedRef — что уже
  // сохранено в этом просмотре (повторное «Скачать все» не дублирует).
  const [albumDl, setAlbumDl] = useState<{ done: number; total: number; saved: number; finished: boolean } | null>(null);
  const albumDlBusyRef = useRef(false);
  const albumSavedRef = useRef<Set<string>>(new Set());
  const albumDlTimerRef = useRef<number | null>(null);
  const albumDlRunRef = useRef(0);
  useEffect(() => {
    albumSavedRef.current = new Set();
    albumDlRunRef.current++; // старая операция больше не трогает состояние нового просмотра
    albumDlBusyRef.current = false;
    setAlbumDl(null);
  }, [lightbox?.messageId]);
  useEffect(() => () => {
    if (albumDlTimerRef.current) window.clearTimeout(albumDlTimerRef.current);
  }, []);
  const downloadAlbumItems = async (items: MediaItem[], rememberSaved = true) => {
    if (albumDlBusyRef.current || items.length === 0) return;
    albumDlBusyRef.current = true;
    const run = albumDlRunRef.current;
    const saved = rememberSaved ? albumSavedRef.current : new Set<string>();
    const alive = () => albumDlRunRef.current === run;
    setLightboxMenuOpen(false);
    if (albumDlTimerRef.current) window.clearTimeout(albumDlTimerRef.current);
    try {
      const pending = items.filter((x) => !saved.has(x.fileId));
      if (pending.length > 0) {
        const authorization = await requestTransferAuthorization({
          direction: 'download',
          label: t(items.length > 1 ? 'lightbox.downloadAll' : 'menu.download'),
          sizeBytes: pending.reduce((sum, x) => sum + (x.sizeBytes ?? 0), 0) || null,
        });
        if (!authorization.allowed) {
          showTransferNotice(authorization.reason ?? t('media.wifiRequired'));
          return;
        }
        if (!alive()) return;
      }
      const result = await downloadSequentially(
        items,
        async (item, i) => {
          const url = await imageSrc(item.fileId);
          const a = document.createElement('a');
          a.href = url;
          a.download = item.fileName || `${item.kind === 'video' ? 'video' : item.kind === 'image' ? 'photo' : 'file'}-${i + 1}${item.kind === 'video' ? '.mp4' : item.kind === 'image' ? '.jpg' : ''}`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          // Пауза, чтобы браузер не счёл серию загрузок спамом
          await new Promise((r) => window.setTimeout(r, 400));
          return true;
        },
        saved,
        (p) => { if (alive()) setAlbumDl({ ...p, finished: false }); },
        (item) => saved.add(item.fileId),
      );
      if (!alive()) return;
      setAlbumDl({ done: result.total, total: result.total, saved: result.saved, finished: true });
      albumDlTimerRef.current = window.setTimeout(() => setAlbumDl(null), 5000);
    } finally {
      if (alive()) albumDlBusyRef.current = false;
    }
  };
  // Клавиши ←/→ (листание) и Esc (закрыть) в просмотрщике. На <video> стрелки оставляем
  // нативной перемотке. Актуальные обработчики берём из ref — эффект подписывается один раз.
  const lightboxKeyRef = useRef<(e: KeyboardEvent) => void>(() => {});
  lightboxKeyRef.current = (e) => {
    if (!lightbox || e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.key === 'Escape') {
      closeLightbox();
    } else if ((e.key === 'ArrowLeft' || e.key === 'ArrowRight') && !(e.target instanceof HTMLVideoElement)) {
      e.preventDefault();
      void lightboxNav(e.key === 'ArrowRight' ? 1 : -1);
    }
  };
  const lightboxOpenFlag = !!lightbox;
  useEffect(() => {
    if (!lightboxOpenFlag) return;
    const h = (e: KeyboardEvent) => lightboxKeyRef.current(e);
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [lightboxOpenFlag]);
  const [chatInfoOpen, setChatInfoOpen] = useState(false);
  // Telegram-style мультизагрузка: перетащенные (drag-and-drop) или выбранные через меню
  // "Прикрепить" файлы сначала оседают тут, пользователь может убрать лишние и добавить
  // общую подпись — и только по кнопке "Отправить" уходят одним сообщением (см. sendAttachments).
  const [pendingAttachments, setPendingAttachments] = useState<StagedAttachment[] | null>(null);
  // Плейсхолдер "Загрузка…" на время sendAttachments (может занять десятки секунд на
  // мобильной сети для крупного видео) — раньше на это время UI не показывал вообще
  // ничего, пользователь не понимал, ушло сообщение или нет. Пропадает сам, когда
  // realtime.send() фиксирует запрос в очереди, которая показывает дальнейшую отправку (см. finally ниже).
  // progress — процент 0-100 по фактически переданным байтам (см. uploadMediaWithProgress),
  // а не по числу завершённых файлов: один крупный файл рядом с несколькими мелкими иначе
  // показывал бы обманчиво медленный прогресс на самом большом вложении.
  // Активные загрузки вложений — массив, а не один объект: если во время загрузки запустить
  // ещё одну (перетащить файлы / прикрепить повторно), прежняя загрузка "сбивалась" — новый
  // setPendingUpload перезаписывал прогресс, а finally первой гасил индикатор (см. отчёт
  // пользователя "если при загрузке ещё запустить загрузку, то первая сбивается"). Каждая
  // загрузка — свой элемент со своим прогрессом.
  const [pendingUploads, setPendingUploads] = useState<{ id: number; count: number; label: string; progress: number }[]>([]);
  const pendingUploadIdRef = useRef(0);
  const [pendingRecordedUploads, setPendingRecordedUploads] = useState<PendingRecordedUpload[]>([]);
  const pendingRecordedIdRef = useRef(0);
  const activeRecordedUploadsRef = useRef(new Set<number>());
  const [dragActive, setDragActive] = useState(false);
  const dragDepthRef = useRef(0);
  // Просмотр закреплённого сообщения "в новом окне" из инфопанели (клик по строке в списке
  // закреплённых) — панель при этом не закрывается, см. onPreviewPinned у ChatInfoModal.
  const [pinnedPreview, setPinnedPreview] = useState<MessageDto | null>(null);
  // Ответ на сообщение (превью над полем ввода) и пересылка (модалка выбора чата)
  const [replyTo, setReplyTo] = useState<MessageDto | null>(null);
  const [forwardIds, setForwardIds] = useState<number[] | null>(null);
  // Превью ссылки над полем ввода — подтягивается по мере набора текста (см. useEffect
  // ниже) и уходит в сообщение сразу при отправке, а не патчится постфактум.
  const [linkPreview, setLinkPreview] = useState<LinkPreviewDto | null>(null);
  const [linkPreviewThumb, setLinkPreviewThumb] = useState<string | null>(null);
  const linkPreviewUrlRef = useRef<string | null>(null);
  const linkPreviewDismissedRef = useRef(false);
  const isGroup = chat?.type === 2;
  const [presence, setPresence] = useState<PresenceDto | null>(null);

  // Живой статус WS-соединения — пока оно разорвано (фоллбэк на polling), в шапке вместо
  // presence/"печатает…" показываем "Соединение…" с бегущими точками, а не устаревший статус.
  const wsOnline = useConnectionStatus();

  // Онлайн-статус собеседника в заголовке — только в личных чатах (в группах там показывается
  // "печатает…", presence неоднозначен для нескольких участников — см. инфопанель группы).
  useEffect(() => {
    if (!chat || chat.type !== 1 || !chat.otherUserId) {
      setPresence(null);
      return;
    }
    const otherUserId = chat.otherUserId;
    let alive = true;
    const poll = () => {
      getPresence(otherUserId)
        .then((p) => alive && setPresence(p))
        .catch(() => {});
    };
    poll();
    const timer = window.setInterval(poll, PRESENCE_POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
    // chat?.type/chat?.otherUserId, а не весь chat — App.tsx пересоздаёт объект чата на каждое
    // входящее сообщение (lastMessage/lastMessageAt), из-за чего presence-поллинг иначе
    // перезапускался бы (и shouldPromptAddContact ниже передёргивался) на каждое сообщение
    // в открытом личном чате, а не только при реальной смене собеседника.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat?.type, chat?.otherUserId]);

  // Баннер «Добавить в контакты?» — для личного чата с человеком, которого ещё нет в контактах
  // (см. отчёт пользователя "проверка есть ли у личного чата контакт в списке контактов...
  // уведомление с двумя кнопками добавить/игнорировать"). Показывать или нет решает сервер
  // (см. ContactService.shouldPromptToAdd) — учитывает и список контактов, и уже отклонённые
  // раньше приглашения, синхронизированные между устройствами (см. отчёт пользователя "если
  // открыть на другом устройстве?" — раньше отказ жил только в localStorage одного браузера).
  // Не показываем в группах и в "Избранном" (там otherUserId пуст).
  const [showContactPrompt, setShowContactPrompt] = useState(false);
  const [contactPromptBusy, setContactPromptBusy] = useState(false);
  useEffect(() => {
    setShowContactPrompt(false);
    if (!chat || chat.type !== 1 || !chat.otherUserId) return;
    const otherUserId = chat.otherUserId;
    let alive = true;
    shouldPromptAddContact(otherUserId)
      .then((show) => alive && setShowContactPrompt(show))
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat?.type, chat?.otherUserId]);
  const addContactFromPrompt = async () => {
    if (!chat?.otherUserId || contactPromptBusy) return;
    setContactPromptBusy(true);
    try {
      await addContact(chat.otherUserId);
      setShowContactPrompt(false);
    } catch {
      /* не критично — баннер просто останется, можно попробовать снова */
    } finally {
      setContactPromptBusy(false);
    }
  };
  const ignoreContactPrompt = async () => {
    if (!chat?.otherUserId) return;
    setShowContactPrompt(false);
    try {
      await dismissAddContactPrompt(chat.otherUserId);
    } catch {
      /* не критично — баннер просто может показаться снова в следующий раз */
    }
  };

  const messagesById = useMemo(() => new Map(messages.map((m) => [m.id, m])), [messages]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<MessageDto[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [activeResultIndex, setActiveResultIndex] = useState<number | null>(null);
  const [highlightId, setHighlightId] = useState<number | null>(null);
  const originalDraftRef = useRef('');
  const editAttemptRef = useRef(0);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [recording, setRecording] = useState(false);
  const previewRecordingRef = useRef(false);
  const [recordedPreview, setRecordedPreview] = useState<PendingRecordedUpload | null>(null);
  const [voiceLocked, setVoiceLocked] = useState(false);
  const voiceHoldRef = useRef<{ gesture: VoiceHold; x: number; y: number; pointerId: number } | null>(null);
  const micStartingRef = useRef(false);
  const micAttemptRef = useRef(0);
  const sendingTextRef = useRef(false);
  const [sendingText, setSendingText] = useState(false);
  // Живая визуализация звука при записи голосового — "бегущая строка" из столбиков, как в
  // Telegram/WhatsApp (см. отчёт пользователя): новый столбик добавляется справа примерно
  // каждые ~90мс на основе громкости (RMS по AnalyserNode), старые уезжают влево и выпадают
  // из фиксированного окна — без этого пользователь не видит, что микрофон вообще пишет звук.
  const [waveLevels, setWaveLevels] = useState<number[]>([]);
  const micAudioCtxRef = useRef<AudioContext | null>(null);
  const micRafRef = useRef<number | null>(null);
  // Мышь запускает запись кликом, сенсор — удержанием; меню отдельно выбирает тип.
  const [armedRecordType, setArmedRecordType] = useState<'voice' | 'video'>(
    me?.defaultRecordType === 'video' ? 'video' : 'voice',
  );
  // Синхронизация при смене пользователя/подгрузке профиля (me приходит с сервера асинхронно).
  useEffect(() => {
    if (me?.defaultRecordType === 'video' || me?.defaultRecordType === 'voice') {
      setArmedRecordType(me.defaultRecordType);
    }
  }, [me?.defaultRecordType]);
  const longPressTimerRef = useRef<number | null>(null);
  const longPressFiredRef = useRef(false);
  const touchRecordingStartedRef = useRef(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingCancelledRef = useRef(false);
  // Автостоп голосового по лимиту 10 минут (отзыв #451) — как в KMP.
  const voiceLimitTimerRef = useRef<number | null>(null);
  // Сколько идёт запись — для подписи «0:07 / 10:00» в панели записи.
  const [voiceElapsedMs, setVoiceElapsedMs] = useState(0);
  // Запись видео-сообщения
  const [videoRecording, setVideoRecording] = useState(false);
  const videoRecorderRef = useRef<MediaRecorder | null>(null);
  const videoChunksRef = useRef<Blob[]>([]);
  const videoRecordingCancelledRef = useRef(false);
  const videoStreamRef = useRef<MediaStream | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);
  const [showGoDown, setShowGoDown] = useState(false);
  const [ctxMenu, setCtxMenu] = useState<
    { x: number; y: number; id: number; mine: boolean; selectionText: string; fileId?: string } | null
  >(null);
  const ctxMenuRef = useRef<HTMLDivElement>(null);
  // Полный пикер эмодзи для реакции (не только QUICK_REACTIONS) — открывается кнопкой "+"
  // в контекстном меню, id того же сообщения, что и ctxMenu.
  const [reactionPickerOpen, setReactionPickerOpen] = useState(false);
  // Вторичные действия доступны в общем меню на любой ширине.
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [headerNotificationsOpen, setHeaderNotificationsOpen] = useState(false);
  const [customMuteOpen, setCustomMuteOpen] = useState(false);
  const [customMuteDateTime, setCustomMuteDateTime] = useState('');
  const { headerRef, callCount } = useHeaderActions();
  const [muteBusy, setMuteBusy] = useState(false);
  // id сообщения, для которого открыт диалог подтверждения закрепления (выбор области — см. PinConfirmModal).
  const [pinConfirmId, setPinConfirmId] = useState<number | null>(null);
  // Карточка участника группы (из контекстного меню его сообщения) — id пользователя, не сообщения.
  const [infoUserId, setInfoUserId] = useState<string | null>(null);
  // Список поставивших реакцию — сами реакции сообщения, не id (после закрытия меню
  // сообщение может обновиться, а тут нужен снимок на момент открытия).
  const [reactionListMsg, setReactionListMsg] = useState<MessageDto | null>(null);
  const [infoMsgId, setInfoMsgId] = useState<number | null>(null);
  // Закреплённые сообщения: общие (для всех) + личные (для себя, видны только мне) — отдельные
  // списки с бэкенда (см. CLAUDE.md/personalPinnedAt — личное не должно течь в общий broadcast).
  const [pinnedShared, setPinnedShared] = useState<MessageDto[]>([]);
  const [pinnedPersonal, setPinnedPersonal] = useState<MessageDto[]>([]);
  const [pinnedBannerIndex, setPinnedBannerIndex] = useState(0);
  // Результаты распознавания речи по id сообщения — живёт здесь, а не в плеере, чтобы
  // не пропадать при перерендере списка сообщений; запуск — из контекстного меню.
  const [transcripts, setTranscripts] = useState<Map<number, TranscriptState>>(new Map());
  const [revealedTranscripts, setRevealedTranscripts] = useState<Set<number>>(new Set());
  // Восстановление уже готовых расшифровок при загрузке истории (после обновления страницы
  // они пропадали — см. отчёт пользователя): лёгкий GET /media/{id}/transcript, без whisper.
  const transcriptPrefetchRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    for (const m of messages) {
      if (transcripts.has(m.id) || transcriptPrefetchRef.current.has(m.id)) continue;
      const fileId = transcribeFileId(m);
      if (!fileId) continue;
      transcriptPrefetchRef.current.add(m.id);
      getTranscriptCached(fileId)
        .then((t) => {
          if (t) setTranscripts((prev) => new Map(prev).set(m.id, { status: 'done', text: t.text }));
        })
        .catch(() => {})
        .finally(() => transcriptPrefetchRef.current.delete(m.id));
    }
  }, [messages, transcripts]);

  // Живые имена авторов пересылки (см. forwardedFromUserId) — payload.data.forwardedFrom
  // сам по себе снэпшот строки на момент пересылки и не обновляется при переименовании
  // автора; подтягиваем актуальное имя лениво, по одному запросу на уникальный userId.
  const [forwardedNames, setForwardedNames] = useState<Map<string, string>>(new Map());
  const forwardedNamesLoadingRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const m of messages) {
      const uid = m.payload.data?.forwardedFromUserId;
      if (!uid || forwardedNames.has(uid) || forwardedNamesLoadingRef.current.has(uid)) continue;
      forwardedNamesLoadingRef.current.add(uid);
      getUserBrief(uid)
        .then((u) => setForwardedNames((prev) => new Map(prev).set(uid, u.displayName)))
        .catch(() => {})
        .finally(() => forwardedNamesLoadingRef.current.delete(uid));
    }
  }, [messages, forwardedNames]);
  useEffect(() => {
    setRevealedTranscripts(new Set());
  }, [chatId]);

  const runTranscribe = useCallback(async (id: number) => {
    const msg = messages.find((m) => m.id === id);
    const fileId = msg && transcribeFileId(msg);
    if (!fileId) return;
    setTranscripts((prev) => new Map(prev).set(id, { status: 'busy' }));
    setRevealedTranscripts((prev) => new Set(prev).add(id));
    try {
      const res = await transcribe(fileId);
      setTranscripts((prev) => new Map(prev).set(id, { status: 'done', text: res.text }));
    } catch (err) {
      setTranscripts((prev) =>
        new Map(prev).set(id, { status: 'error', error: err instanceof Error ? err.message : t('transcriptionFailed') }),
      );
    }
  }, [messages, t]);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [mediaError, setMediaError] = useState('');
  const [cloudLimitMessage, setCloudLimitMessage] = useState<string | null>(null);
  const [toast, setToast] = useState('');
  const toastTimerRef = useRef<number | null>(null);
  // Мультивыбор сообщений: на мобильных — вместо мелкого контекстного меню, через долгое
  // нажатие; на десктопе — отдельной кнопкой "Выбрать" в шапке (там контекстное меню и так есть).
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const currentUserId = me?.id ?? null;
  const [memberRoleSnapshot, setMemberRoleSnapshot] = useState<{ chatId: string; userId: string; role: number | null } | null>(null);
  const currentChatRole = memberRoleSnapshot?.chatId === chatId && memberRoleSnapshot.userId === currentUserId
    ? memberRoleSnapshot.role
    : null;

  useEffect(() => {
    if (chat?.type !== 2 || !currentUserId) return;
    let active = true;
    let requestVersion = 0;
    const refreshRole = () => {
      const version = ++requestVersion;
      listMembers(chatId).then((members) => {
        if (!active || version !== requestVersion) return;
        const role = members.find((member) => member.user.id === currentUserId)?.role ?? null;
        setMemberRoleSnapshot({ chatId, userId: currentUserId, role });
      }).catch(() => {
        if (active && version === requestVersion) setMemberRoleSnapshot({ chatId, userId: currentUserId, role: null });
      });
    };
    refreshRole();
    const unsubscribe = realtime.onEvent((eventChatId, raw) => {
      const event = raw as { type?: string; user_id?: string };
      if (eventChatId === chatId && event.type === 'member_role_changed' && event.user_id === currentUserId) {
        refreshRole();
      }
    });
    return () => { active = false; unsubscribe(); };
  }, [chat?.type, chatId, currentUserId]);

  const canDeleteMessage = (senderId: string) => {
    if (!currentUserId) return false;
    if (senderId === currentUserId) return true;
    if (chat?.type === 1) return true; // В личном чате оба участника удаляют чужие сообщения.
    return chat?.type === 2 && (currentChatRole === 1 || currentChatRole === 2);
  };

  const showToast = (text: string) => {
    setToast(text);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(''), 2500);
  };

  const changeChatMute = async (mode: ChatMuteMode, muteUntil?: string) => {
    if (muteBusy) return;
    setHeaderMenuOpen(false);
    setMuteBusy(true);
    try {
      await setChatMute(chatId, mode, muteUntil);
      onChatsChanged();
    } catch {
      showToast(t('header.muteFailed'));
    } finally {
      setMuteBusy(false);
    }
  };

  const copyText = async (text: string) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      showToast(t('copied'));
    } catch {
      showToast(t('copyFailed'));
    }
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const toggleSelect = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      if (next.size === 0) setSelectMode(false);
      return next;
    });
  }, []);

  // Клик сбоку переключает сообщения только после явного входа в выбор.
  const handleMsgBodyClick = (id: number) => {
    if (selectMode) toggleSelect(id);
  };

  const copySelected = () => {
    const ordered = messages.filter((m) => selectedIds.has(m.id));
    const text = ordered.map(copyableMessageText).filter(Boolean).join('\n\n');
    if (text) copyText(text);
    exitSelectMode();
  };

  const openContextMenu = useCallback((id: number, x: number, y: number, fileId?: string) => {
    // Раньше на мобильном (≤720px) долгое нажатие сразу уводило в режим выбора, минуя
    // это меню целиком — а поставить РЕАКЦИЮ (единственный способ — верхняя строка этого
    // меню) с телефона было вообще нечем. Явный вход в выбор остался — кнопка "Выбрать"
    // в шапке чата (см. .call-mini[title={t('header.select')}]) работает на любом экране одинаково.
    const msg = messages.find((m) => m.id === id);
    // Не даём меню выйти за пределы окна
    const MENU_W = 190;
    const MENU_H = 210;
    const nx = Math.max(4, Math.min(x, window.innerWidth - MENU_W));
    const ny = Math.max(4, Math.min(y, window.innerHeight - MENU_H));
    // Если у пользователя было активное выделение текста — right-click "Копировать"
    // копирует именно его (как в нативном меню браузера), а не всё сообщение целиком.
    const selectionText = window.getSelection()?.toString() ?? '';
    setReactionPickerOpen(false);
    setCtxMenu({ x: nx, y: ny, id, mine: msg?.senderId === me?.id, selectionText, fileId });
  }, [messages, me?.id]);

  // Точная коррекция позиции по РЕАЛЬНЫМ размерам меню (высота "плавает" — реакции,
  // редактировать/удалить только у своих и т.д., фиксированная оценка выше — лишь грубая
  // прикидка для первого кадра). Зависимости — id/mine/selectionText, а не x/y: иначе
  // собственная же коррекция x/y запускала бы эффект повторно по кругу.
  useLayoutEffect(() => {
    if (!ctxMenu || !ctxMenuRef.current) return;
    const rect = ctxMenuRef.current.getBoundingClientRect();
    const maxX = Math.max(4, window.innerWidth - rect.width - 4);
    const maxY = Math.max(4, window.innerHeight - rect.height - 4);
    const nx = Math.min(ctxMenu.x, maxX);
    const ny = Math.min(ctxMenu.y, maxY);
    if (nx !== ctxMenu.x || ny !== ctxMenu.y) {
      setCtxMenu((prev) => (prev ? { ...prev, x: nx, y: ny } : prev));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxMenu?.id, ctxMenu?.mine, ctxMenu?.selectionText, currentChatRole]);

  const goToMessage = useCallback((id: number) => {
    const el = document.getElementById('msg-' + id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightId(id);
      setTimeout(() => setHighlightId(null), 2000);
    }
  }, []);

  // Переход к сообщению из глобального поиска (открыли чат из результатов) — то же best-effort
  // поведение, что у обычного goToMessage: если сообщение ещё не в загруженной странице
  // истории, попытка молча не срабатывает при этом рендере, но повторяется на следующих (по
  // мере подгрузки истории/новых сообщений), пока не найдётся или jumpToMessageId не сбросят.
  useEffect(() => {
    if (jumpToMessageId == null) return;
    const el = document.getElementById('msg-' + jumpToMessageId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightId(jumpToMessageId);
      setTimeout(() => setHighlightId(null), 2000);
      onJumpHandled?.();
    }
  }, [jumpToMessageId, messages, onJumpHandled]);

  // Живой поиск по мере ввода (debounce 300 мс) — без отдельной кнопки "Найти".
  useEffect(() => {
    if (!searchOpen) return;
    const q = searchQuery.trim();
    setSearchError('');
    if (!q) {
      setSearchResults([]);
      setActiveResultIndex(null);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const res = await searchMessages(chatId, q);
        setSearchResults(res);
        setActiveResultIndex(res.length ? 0 : null);
      } catch (error) {
        setSearchError(error instanceof ApiError ? error.message : t('search.failed'));
        setSearchResults([]);
        setActiveResultIndex(null);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery, searchOpen, chatId, t]);

  const jumpToResult = (idx: number) => {
    if (idx < 0 || idx >= searchResults.length) return;
    setActiveResultIndex(idx);
    goToMessage(searchResults[idx].id);
  };
  const goToPrevResult = () => {
    if (activeResultIndex == null || !searchResults.length) return;
    jumpToResult((activeResultIndex - 1 + searchResults.length) % searchResults.length);
  };
  const goToNextResult = () => {
    if (activeResultIndex == null || !searchResults.length) return;
    jumpToResult((activeResultIndex + 1) % searchResults.length);
  };
  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setActiveResultIndex(null);
  };

  // Авто-рост поля ввода до 4 строк
  const autoResize = () => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = Math.min(ta.scrollHeight, 104) + 'px';
  };

  const insertEmoji = (e: string) => {
    setText((t) => t + e);
    setEmojiOpen(false);
    setTimeout(() => {
      taRef.current?.focus();
      autoResize();
    }, 0);
  };

  // Скролл вниз: мгновенно (до отрисовки кадра) при смене чата или первой подгрузке его
  // истории, плавно — когда чат уже открыт и просто пришло новое сообщение.
  // .messages не перемонтируется при смене chatId, поэтому scrollTop от предыдущего,
  // например более длинного, чата иначе "утаскивал" вид не в конец нового списка.
  // Ключ ленты: чат + комната. Смена комнаты внутри того же чата — это новая лента, её надо позиционировать заново.
  const feedKey = `${chatId}:${roomId ?? 'main'}`;
  const feedKeyRef = useRef(feedKey);
  feedKeyRef.current = feedKey;
  const emptyFeedAttemptsRef = useRef(0);
  const emptyFeedKeyRef = useRef(feedKey);
  useEffect(() => {
    if (emptyFeedKeyRef.current !== feedKey) {
      emptyFeedKeyRef.current = feedKey;
      emptyFeedAttemptsRef.current = 0;
    }
    if (messages.length > 0) {
      emptyFeedAttemptsRef.current = 0;
      return;
    }
    if (!historyLoaded || loadingOlderRef.current) return;

    let cancelled = false;
    const maxEmptyFeedIterations = 4;
    loadingOlderRef.current = true;
    const loadUntilVisible = async () => {
      try {
        await loadEmptyHistoryUntilVisible(async () => {
          emptyFeedAttemptsRef.current++;
          return onLoadOlder(chatId, roomId);
        }, maxEmptyFeedIterations - emptyFeedAttemptsRef.current, () => cancelled);
      } finally {
        if (feedKeyRef.current === feedKey) loadingOlderRef.current = false;
      }
    };
    void loadUntilVisible().catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [chatId, feedKey, historyLoaded, messages.length, onLoadOlder, roomId]);
  const prevChatRef = useRef<string | null>(null);
  const scrollSettledRef = useRef<string | null>(null);
  // Сколько сообщений было непрочитано на момент открытия ТЕКУЩЕГО чата (см. unreadOnOpen
  // выше) — если > 0, самый первый скролл ставим не в конец, а на первое непрочитанное (см.
  // отчёт пользователя "если прочитано до какого-то сообщения, чат должен открываться на
  // этом же месте"). Сбрасывается при каждой смене чата, "потребляется" один раз при
  // первом успешном скролле.
  const pendingUnreadRef = useRef(0);
  const unreadItems = useMemo(() => unreadMessages(messages, unreadCount, me?.id), [messages, unreadCount, me?.id]);
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || !onVisibleRead) return;
    const checkVisible = () => {
      if (document.hidden || scroller.getClientRects().length === 0) return;
      const viewport = scroller.getBoundingClientRect();
      let lastVisible = 0;
      for (const message of unreadItems) {
        const element = document.getElementById('msg-' + message.id);
        if (!element) continue;
        const rect = element.getBoundingClientRect();
        if (rect.bottom > viewport.top && rect.top < viewport.bottom) lastVisible = message.id;
      }
      if (lastVisible > 0) onVisibleRead(lastVisible);
    };
    const observer = new IntersectionObserver(checkVisible, { root: scroller });
    for (const message of unreadItems) {
      const element = document.getElementById('msg-' + message.id);
      if (element) observer.observe(element);
    }
    scroller.addEventListener('scroll', checkVisible);
    document.addEventListener('visibilitychange', checkVisible);
    checkVisible();
    return () => {
      observer.disconnect();
      scroller.removeEventListener('scroll', checkVisible);
      document.removeEventListener('visibilitychange', checkVisible);
    };
  }, [feedKey, unreadItems, onVisibleRead]);

  const handleGoDown = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const unreadIds = unreadItems.map(message => message.id);
    const target = scrollDownTarget(messages, unreadIds);
    const unreadTarget = target != null && unreadIds.includes(target) ? document.getElementById('msg-' + target) : null;
    pinnedToBottomRef.current = !unreadTarget;
    beginProgrammaticScroll();
    if (unreadTarget) {
      unreadTarget.scrollIntoView({ block: 'start', behavior: 'smooth' });
    } else {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };
  // "Прижаты к низу": пока true, любой рост высоты контейнера (например, картинки/аватарки
  // догрузились по сети уже ПОСЛЕ первого кадра и раздвинули сообщения) переприжимает вид
  // обратно вниз — иначе итоговая позиция оказывалась намного выше реального конца чата,
  // вплоть до самого верха, если догружаемого медиа было много.
  const pinnedToBottomRef = useRef(true);
  // Подавляем реакцию handleScroll на скролл-события, которые вызвали МЫ САМИ (программный
  // прижим к низу), а не пользователь. Без этого гонка ломала автоскролл насовсем: пока
  // грузится несколько медиа подряд (картинки/видео/декод голосового через Web Audio API),
  // между нашим el.scrollTop = el.scrollHeight и срабатыванием 'scroll' контейнер успевал
  // подрасти от ещё одной догрузившейся картинки — handleScroll видел устаревший scrollTop
  // относительно уже большего scrollHeight, считал, что мы "не внизу", и НАВСЕГДА снимал
  // pinnedToBottomRef, хотя пользователь ничего не трогал. Чат замирал на середине истории.
  const suppressScrollCheckRef = useRef(false);
  const suppressTimerRef = useRef<number | null>(null);
  // Момент последнего ввода пользователя в ленте (колесо, касание, мышь, клавиши). Скролл-событие
  // без недавнего ввода — не жест пользователя, а следствие раскладки (догрузились кэшированные
  // медиа и раздвинули ленту за один кадр): такое событие не должно снимать «прижатие к низу».
  const lastUserInputRef = useRef(0);
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const mark = () => {
      lastUserInputRef.current = performance.now();
    };
    const evs = ['wheel', 'touchstart', 'touchmove', 'pointerdown', 'keydown'] as const;
    evs.forEach((n) => el.addEventListener(n, mark, { passive: true }));
    return () => evs.forEach((n) => el.removeEventListener(n, mark));
  }, []);
  const beginProgrammaticScroll = () => {
    suppressScrollCheckRef.current = true;
    if (suppressTimerRef.current) window.clearTimeout(suppressTimerRef.current);
    suppressTimerRef.current = window.setTimeout(() => {
      suppressScrollCheckRef.current = false;
    }, 400);
  };
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (prevChatRef.current !== feedKey) {
      prevChatRef.current = feedKey;
      scrollSettledRef.current = null;
      pinnedToBottomRef.current = true;
      // Непрочитанные известны только для основной ленты (у комнат своего счётчика нет).
      pendingUnreadRef.current = roomId ? 0 : unreadOnOpen;
    }
    if (scrollSettledRef.current !== feedKey) {
      beginProgrammaticScroll();
      const unread = pendingUnreadRef.current;
      let landedOnUnread = false;
      if (unread > 0 && messages.length > 0) {
        const idx = Math.max(0, messages.length - unread);
        // Если элемент ещё не появился в DOM, ищем ближайшее следующее сообщение.
        let targetEl: HTMLElement | null = null;
        for (let i = idx; i < messages.length; i++) {
          const cand = document.getElementById('msg-' + messages[i].id);
          if (cand) {
            targetEl = cand;
            break;
          }
        }
        if (targetEl) {
          targetEl.scrollIntoView({ block: 'start' });
          // Ниже точки прочтения есть непрочитанные — не "прижаты к низу": рост контента
          // (картинки/аватарки) не должен утаскивать вид обратно вниз, а кнопка "в конец"
          // должна быть видна.
          pinnedToBottomRef.current = false;
          setShowGoDown(true);
          landedOnUnread = true;
        }
      }
      if (!landedOnUnread) {
        el.scrollTop = el.scrollHeight;
        // Новая лента открыта внизу — стрелка «в конец» от прежней ленты не должна оставаться
        // (её скролл-событие подавлено как программное и сам handleScroll стрелку не обновит).
        setShowGoDown(false);
      }
      if (messages.length > 0) {
        scrollSettledRef.current = feedKey;
        pendingUnreadRef.current = 0;
      }
    } else if (pinnedToBottomRef.current) {
      beginProgrammaticScroll();
      // В фоновой вкладке smooth-анимация замирает (rAF не идёт), а окно подавления 400 мс
      // истекает раньше — промежуточные scroll-события снимали прижатие (сбой позиции после
      // возврата на вкладку, отзыв #134). Пока вкладка скрыта — мгновенно.
      bottomRef.current?.scrollIntoView({ behavior: document.hidden ? 'auto' : 'smooth' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length, feedKey]);

  // Возврат на вкладку: если перед уходом лента была прижата к низу — снова прижимаем мгновенно
  // (за время простоя пришли сообщения/догон /sync, картинки перекомпоновались), не полагаясь на
  // scroll-события, которые браузер мог отдать вразнобой.
  const wasPinnedOnHideRef = useRef(false);
  useEffect(() => {
    const onVisibility = () => {
      const el = scrollerRef.current;
      if (document.hidden) {
        wasPinnedOnHideRef.current = pinnedToBottomRef.current;
        return;
      }
      if (!el || !wasPinnedOnHideRef.current) return;
      pinnedToBottomRef.current = true;
      beginProgrammaticScroll();
      el.scrollTop = el.scrollHeight;
      setShowGoDown(false);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Подстраховка поверх ResizeObserver ниже: превью видео/голосовых, аватарки и декодирование
  // голосового через Web Audio API иногда меняют высоту позже, чем успевает сработать сам
  // ResizeObserver (см. отчёт пользователя "чат открывается не в самом низу, а чуть выше —
  // приходится перематывать вниз"). Несколько отложенных повторных прижимов к низу после
  // переключения чата — только пока пользователь и так внизу (pinnedToBottomRef).
  useEffect(() => {
    const timers = [150, 500, 1200].map((delay) =>
      window.setTimeout(() => {
        const el = scrollerRef.current;
        if (!el || chatIdRef.current !== chatId || !pinnedToBottomRef.current) return;
        if (el.scrollHeight - el.scrollTop - el.clientHeight < 2) return;
        beginProgrammaticScroll();
        el.scrollTop = el.scrollHeight;
      }, delay),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [chatId, roomId]);

  // Вход/выход из режима выделения оборачивает каждое сообщение в строку с чекбоксом
  // (см. .msg-select-row) — переверстка (особенно у медиа-сеток) резко меняет высоту списка,
  // а scrollTop сам не пересчитывается: тот же пиксельный отступ указывает уже на более
  // раннюю точку истории, и внизу списка чат визуально "подскакивает" вверх (отчёт
  // пользователя "при выборе сообщения происходит смещение чата вверх"). Синхронно (до
  // отрисовки кадра) переприжимаем вниз, если пользователь и так был внизу.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el || !pinnedToBottomRef.current) return;
    beginProgrammaticScroll();
    el.scrollTop = el.scrollHeight;
  }, [selectMode]);

  useEffect(() => {
    const el = scrollerRef.current;
    const inner = messagesInnerRef.current;
    if (!el || !inner || typeof ResizeObserver === 'undefined') return;
    // Следим за высотой ВНУТРЕННЕГО контейнера (не .messages — у неё overflow:auto,
    // её собственный box не меняется, когда внутри догружаются картинки/аватарки).
    const ro = new ResizeObserver(() => {
      if (pinnedToBottomRef.current) {
        beginProgrammaticScroll();
        el.scrollTop = el.scrollHeight;
      }
    });
    ro.observe(inner);
    // Высота самого скроллера меняется при возврате на вкладку/появлении адресной строки или
    // клавиатуры на телефоне — низ уходит из вида, хотя лента формально «прижата».
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Черновик: какому ключу (пользователь+чат+комната) принадлежит текущий text. Пока ключ в ref не
  // совпадает с текущим (render после смены чата/комнаты, но до сброса ниже), сохранение пропускаем —
  // иначе текст прежней комнаты записался бы черновиком новой.
  const draftOwnerRef = useRef<string | null>(null);
  const draftScope = me ? `${me.id}:${chatId}:${roomId ?? 'main'}` : null;

  // Сброс редактирования при смене чата ИЛИ комнаты (раньше текст/ответ/правка «протекали» в соседнюю комнату)
  useEffect(() => {
    editAttemptRef.current++;
    originalDraftRef.current = '';
    setEditingId(null);
    setReplyTo(null);
    setForwardIds(null);
    setText(me ? getDraft(me.id, chatId, roomId) : '');
    draftOwnerRef.current = draftScope;
    clearLinkPreview();
    setRecordedPreview(null);
    previewRecordingRef.current = false;
    setSelectMode(false);
    setSelectedIds(new Set());
    // Останавливаем (и отменяем, не отправляем) запись видео/аудио при уходе из чата
    videoRecordingCancelledRef.current = true;
    if (videoRecorderRef.current?.state === 'recording') videoRecorderRef.current.stop();
    setVideoRecording(false);
    micAttemptRef.current++;
    voiceHoldRef.current?.gesture.release(true);
    recordingCancelledRef.current = true;
    if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
    setRecording(false);
    // Подгрузка истории предыдущего чата могла быть в полёте на момент переключения — сама
    // await-коррекция скролла себя не применит (см. chatIdRef-проверку в handleScroll), но
    // флаг всё равно сбрасываем сразу, не дожидаясь резолва того запроса.
    loadingOlderRef.current = false;
    // Таймер долгого нажатия на кнопку записи не привязан к chatId — без явной отмены он всё
    // равно сработает после свитча чата и откроет rec-picker уже поверх нового чата.
    if (longPressTimerRef.current != null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    setCtxMenu(null);
    setLightbox(null);
    setPinnedPreview(null);
    setReactionListMsg(null);
    setInfoUserId(null);
    setPinConfirmId(null);
    setHeaderMenuOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- me/draftScope берём на момент смены чата/комнаты
  }, [chatId, roomId, me?.id]);

  // Сохранение черновика при каждом изменении текста (localStorage, пустой текст удаляет черновик).
  // В режиме правки сообщения черновик не трогаем.
  useEffect(() => {
    if (!me || editingId != null || draftOwnerRef.current !== draftScope) return;
    setDraft(me.id, chatId, roomId, text);
  }, [text, editingId, me, chatId, roomId, draftScope]);

  // Полное размонтирование ChatView (закрытие чата, не смена chatId) во время активной записи
  // не проходит через эффект выше — без этого cleanup поток микрофона/камеры остаётся
  // захваченным навсегда, индикатор записи в ОС/браузере горит бессрочно.
  useEffect(() => {
    return () => {
      // Это счётчик попыток, а не DOM-ref: отменяем именно текущий запрос разрешения.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      micAttemptRef.current++;
      voiceHoldRef.current?.gesture.release(true);
      recordingCancelledRef.current = true;
      if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
      videoRecordingCancelledRef.current = true;
      videoRecorderRef.current?.stop();
    };
  }, []);

  // Подгрузка старых сообщений при скролле вверх + кнопка «в конец»
  const handleScroll = async () => {
    const el = scrollerRef.current;
    if (!el) return;
    if (suppressScrollCheckRef.current) {
      // Программный скролл всегда ставит scrollTop ровно "к низу" — если текущая позиция и
      // правда там, это эхо нашего же вызова (см. beginProgrammaticScroll), продлеваем окно
      // подавления. Но при серии догрузок медиа подряд (несколько ResizeObserver-срабатываний
      // за доли секунды) окно могло бы продлеваться бесконечно, целиком игнорируя реальную
      // попытку пользователя проскроллить вверх в этот момент — поэтому если позиция уже
      // заметно ушла от низа, считаем это реальным действием пользователя и не подавляем его.
      // Порог — 400px, а не условные "чуть-чуть": одно фото/видео-сообщение (.msg img
      // max-height 320px + отступы/время) само по себе даёт скачок такого порядка между нашим
      // el.scrollTop = el.scrollHeight и обработкой результирующего 'scroll' — на мобильной
      // сети, где медиа догружается медленнее и не одним кадром, именно такой одиночный
      // скачок ошибочно читался как "пользователь заметно отскроллил", навсегда снимал
      // pinnedToBottomRef, и чат замирал на середине истории вместо конца (см. отчёт
      // пользователя "на мобильной версии не с последнего сообщения, а где-то с середины").
      const stillNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 400;
      const userDriven = performance.now() - lastUserInputRef.current < 400;
      if (!userDriven && pinnedToBottomRef.current && !stillNearBottom) {
        // Скачок высоты без участия пользователя (кэшированные картинки/видео подгрузились в
        // том же кадре, что и прижим к низу): остаёмся внизу, а не считаем это уходом вверх.
        el.scrollTop = el.scrollHeight;
        setShowGoDown(false);
        return;
      }
      if (stillNearBottom) {
        setShowGoDown(el.scrollHeight - el.scrollTop - el.clientHeight >= 80);
        if (suppressTimerRef.current) window.clearTimeout(suppressTimerRef.current);
        suppressTimerRef.current = window.setTimeout(() => {
          suppressScrollCheckRef.current = false;
        }, 400);
        return;
      }
      suppressScrollCheckRef.current = false;
    }
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setShowGoDown(!nearBottom);
    pinnedToBottomRef.current = nearBottom;
    if (loadingOlderRef.current) return;
    if (el.scrollTop < 40) {
      loadingOlderRef.current = true;
      const loadingForChatId = chatId;
      const prevHeight = el.scrollHeight;
      await onLoadOlder(chatId, roomId).catch(() => {});
      // Пользователь мог переключиться на другой чат, пока запрос был в полёте — скроллер тот
      // же DOM-узел (ChatView не перемонтируется при смене chatId), но внутри уже сообщения
      // ДРУГОГО чата: коррекция scrollTop по старой высоте её сломает. loadingOlderRef для
      // нового чата уже сброшен эффектом смены chatId — здесь его трогать не нужно.
      if (chatIdRef.current !== loadingForChatId) return;
      el.scrollTop = el.scrollHeight - prevHeight + el.scrollTop;
      loadingOlderRef.current = false;
    }
  };

  const handleEdit = (id: number) => {
    const msg = messages.find((m) => m.id === id);
    if (!msg) return;
    if (editingId == null) {
      originalDraftRef.current = text;
      if (me) setDraft(me.id, chatId, roomId, text);
    }
    editAttemptRef.current++;
    setReplyTo(null);
    setEditingId(id);
    setText((msg.payload?.data?.text as string | undefined) ?? '');
    setTimeout(() => taRef.current?.focus(), 0);
  };
  const handleDelete = (id: number) => {
    setConfirmDeleteId(id);
  };
  const startReply = (msg: MessageDto) => {
    editAttemptRef.current++;
    setEditingId(null);
    setText(editingId != null ? originalDraftRef.current : text);
    setReplyTo(msg);
    setTimeout(() => taRef.current?.focus(), 0);
  };
  const handleReply = (id: number) => {
    const msg = messages.find((m) => m.id === id);
    if (!msg) return;
    startReply(msg);
  };

  // Ответ на закреплённое прямо из модалки предпросмотра (инфопанель → "Подключиться"/баннер) —
  // раньше единственная кнопка "Ответить" просто закрывала модалку и передавала эстафету
  // основному композеру внизу чата. Теперь это свой мини-композер (тот же .composer/.ta-wrap/
  // .send-btn, что и у AttachPreviewModal) — отправляет сам, без выхода из модалки.
  const [pinnedReplyText, setPinnedReplyText] = useState('');
  const [pinnedReplySending, setPinnedReplySending] = useState(false);
  const [pinnedReplyEmojiOpen, setPinnedReplyEmojiOpen] = useState(false);
  const pinnedReplyTaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setPinnedReplyText('');
    setPinnedReplyEmojiOpen(false);
  }, [pinnedPreview?.id]);

  const insertPinnedReplyEmoji = (e: string) => {
    setPinnedReplyText((t) => t + e);
    setPinnedReplyEmojiOpen(false);
    setTimeout(() => pinnedReplyTaRef.current?.focus(), 0);
  };

  const sendPinnedReply = async () => {
    const value = pinnedReplyText.trim();
    if (!value || !pinnedPreview || pinnedReplySending) return;
    setPinnedReplySending(true);
    try {
      await realtime.send(chatId, {
        clientMsgId: randomUUID(),
        type: 1,
        payload: { alg: 'plaintext', data: { text: value } },
        replyToId: pinnedPreview.id,
        roomId,
      }, sendSession);
      setPinnedReplyText('');
      setPinnedPreview(null);
    } catch {
      /* ошибка отправки — текст остаётся в поле, можно попробовать ещё раз */
    } finally {
      setPinnedReplySending(false);
    }
  };

  // Обновление UI — через message_edited (WS/polling), как и для остального (правка/удаление):
  // тот же round-trip, локально ничего не патчим, чтобы не разъезжаться с сервером.
  const reactToMessage = (id: number, emoji: string) => {
    setMessageReaction(chatId, id, emoji).catch((error) => {
      showToast(error instanceof ApiError ? error.message : t('reactionFailed'));
    });
  };

  // Общее закрепление ("для всех") видно всем через message_edited — как реакции, без
  // локального патча, просто ждём round-trip (см. эффект ниже, следящий за pinnedAt в messages).
  const togglePinShared = (id: number, pinned: boolean) => {
    setMessagePin(chatId, id, 'all', pinned).catch((error) => {
      showToast(error instanceof ApiError ? error.message : t('pinFailed'));
    });
  };

  // Личное закрепление ("для себя") НЕ рассылается остальным (см. CLAUDE.md-стиль комментарий
  // в MessageDto.personalPinnedAt на бэкенде) — round-trip'а не будет, поэтому обновляем список
  // сами по ответу API.
  const togglePinPersonal = async (id: number, pinned: boolean) => {
    try {
      await setMessagePin(chatId, id, 'me', pinned);
      const list = await getPinnedMessages(chatId, 'me');
      setPinnedPersonal(list);
    } catch (error) {
      showToast(error instanceof ApiError ? error.message : t('personalPinFailed'));
    }
  };

  // Личные закреплённые — грузим отдельно один раз на чат (не транслируются, поэтому их не
  // получить из общего потока событий messages/message_edited).
  useEffect(() => {
    let alive = true;
    getPinnedMessages(chatId, 'me').then((list) => alive && setPinnedPersonal(list)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [chatId]);

  // Общие закреплённые — как только у любого сообщения из уже загруженного окна истории
  // меняется pinnedAt (через message_edited round-trip), перезапрашиваем полный список: он
  // может включать сообщения вне текущего окна истории, точечный локальный патч был бы неполным.
  const pinnedFingerprint = useMemo(
    () => messages.filter((m) => m.pinnedAt).map((m) => m.id + ':' + m.pinnedAt).join(','),
    [messages],
  );
  useEffect(() => {
    let alive = true;
    getPinnedMessages(chatId, 'all').then((list) => alive && setPinnedShared(list)).catch(() => {});
    return () => {
      alive = false;
    };
  }, [chatId, pinnedFingerprint]);

  const allPinned = useMemo(
    () => mergePinnedMessages(pinnedShared, pinnedPersonal),
    [pinnedShared, pinnedPersonal],
  );

  useEffect(() => {
    setPinnedBannerIndex(0);
  }, [chatId]);

  const currentPinned = allPinned.length ? allPinned[pinnedBannerIndex % allPinned.length] : null;

  const onPinnedBannerClick = () => {
    if (!currentPinned) return;
    goToMessage(currentPinned.msg.id);
    if (allPinned.length > 1) setPinnedBannerIndex((i) => (i + 1) % allPinned.length);
  };

  const onUnpinCurrent = () => {
    if (!currentPinned) return;
    if (currentPinned.scope === 'me') togglePinPersonal(currentPinned.msg.id, false);
    else togglePinShared(currentPinned.msg.id, false);
  };

  // Единый обработчик для инфо-панели (там оба вида закреплений в одном списке).
  const togglePin = (id: number, scope: 'all' | 'me', pinned: boolean) => {
    if (scope === 'me') togglePinPersonal(id, pinned);
    else togglePinShared(id, pinned);
  };

  const doForward = async (targetChatIds: string[]) => {
    const ids = forwardIds;
    if (!ids || ids.length === 0) return;
    const toSend = messages.filter((m) => ids.includes(m.id) && m.type !== 99);
    let failed = 0;
    const total = targetChatIds.length * toSend.length;
    for (const targetId of targetChatIds) {
      for (const m of toSend) {
        try {
          await realtime.send(targetId, {
            clientMsgId: randomUUID(),
            type: m.type,
            payload: {
              alg: m.payload.alg,
              data: {
                ...m.payload.data,
                forwardedFrom: m.senderName || (m.senderId === me?.id ? me?.displayName : null),
                forwardedFromUserId: m.senderId,
              },
            },
          }, sendSession);
        } catch {
          // ошибка доставки одного из сообщений — остальные всё равно пробуем отправить,
          // но итоговый тост должен отражать реальный результат, а не всегда "Переслано".
          failed++;
        }
      }
    }
    setForwardIds(null);
    exitSelectMode();
    if (failed === 0) showToast(t('forwarded'));
    else if (failed === total) showToast(t('forwardFailed'));
    else showToast(t('forwardPartial', { sent: total - failed, total }));
  };

  const cancelRecording = () => {
    micAttemptRef.current++;
    voiceHoldRef.current?.gesture.release(true);
    // Флаг, а не просто очистка chunksRef: stop() асинхронно вызывает ещё один
    // ondataavailable ПОСЛЕ этой функции — он бы дописал в уже очищенный массив
    // финальный чанк, и запись всё равно ушла бы на сервер.
    recordingCancelledRef.current = true;
    if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
    setRecording(false);
  };

  // Превью ссылки над полем ввода — по мере набора текста, с задержкой в 500мс после
  // появления/изменения URL (не после каждой клавиши: пока правится остальной текст,
  // одна и та же ссылка не перезапрашивается заново, см. сравнение с linkPreviewUrlRef).
  useEffect(() => {
    const url = firstUrl(text);
    if (url === linkPreviewUrlRef.current) return;
    linkPreviewUrlRef.current = url;
    linkPreviewDismissedRef.current = false;
    setLinkPreview(null);
    if (!url) return;
    const timer = window.setTimeout(async () => {
      try {
        const preview = await fetchLinkPreview(url);
        if (linkPreviewUrlRef.current === url && !linkPreviewDismissedRef.current && hasUsefulPreview(preview)) {
          setLinkPreview(preview);
        }
      } catch {
        /* не получилось — просто не покажем превью, ссылка в тексте и так кликабельна */
      }
    }, 500);
    return () => window.clearTimeout(timer);
  }, [text]);

  useEffect(() => {
    if (!linkPreview?.imageFileId) {
      setLinkPreviewThumb(null);
      return;
    }
    let alive = true;
    imageSrc(linkPreview.imageFileId)
      .then((u) => alive && setLinkPreviewThumb(u))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [linkPreview?.imageFileId]);

  const dismissLinkPreview = () => {
    linkPreviewDismissedRef.current = true;
    setLinkPreview(null);
  };

  const clearLinkPreview = () => {
    linkPreviewUrlRef.current = null;
    linkPreviewDismissedRef.current = false;
    setLinkPreview(null);
  };

  // "Печатает…" — шлём true при первом символе, потом сбрасываем в false либо по таймауту
  // бездействия (собеседник просто перестал печатать), либо сразу при очистке поля/отправке.
  const TYPING_STOP_MS = 3000;
  const stopTyping = () => {
    if (typingStopTimerRef.current) {
      window.clearTimeout(typingStopTimerRef.current);
      typingStopTimerRef.current = null;
    }
    if (typingActiveRef.current) {
      typingActiveRef.current = false;
      realtime.publish(`/app/chats/${chatId}/typing`, { isTyping: false });
    }
  };
  const onComposerInput = (value: string) => {
    if (!value.trim()) {
      stopTyping();
      return;
    }
    if (!typingActiveRef.current) {
      typingActiveRef.current = true;
      realtime.publish(`/app/chats/${chatId}/typing`, { isTyping: true });
    }
    if (typingStopTimerRef.current) window.clearTimeout(typingStopTimerRef.current);
    typingStopTimerRef.current = window.setTimeout(stopTyping, TYPING_STOP_MS);
  };
  // Смена чата/размонтирование во время набора — не оставлять собеседника с "печатает…" навечно.
  // stopTyping не мемоизирован: нужна именно версия с chatId уходящего чата, перезапуск по stopTyping сломал бы cleanup.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => stopTyping, [chatId]);

  const sendText = async () => {
    const value = text.trim();
    if (!value || sendingTextRef.current) return;
    stopTyping();
    // Режим редактирования: обновляем существующее сообщение
    if (editingId != null) {
      const attempt = ++editAttemptRef.current;
      sendingTextRef.current = true;
      setSendingText(true);
      try {
        await onEditMessage(chatId, editingId, value);
        if (attempt !== editAttemptRef.current) return;
        setEditingId(null);
        setText(originalDraftRef.current);
        autoResize();
      } catch (error) {
        if (attempt === editAttemptRef.current) showToast(error instanceof ApiError ? error.message : t('editFailed'));
      } finally {
        sendingTextRef.current = false;
        setSendingText(false);
      }
      return;
    }
    const replyToId = replyTo?.id ?? null;
    // Превью уже подтянуто (или явно убрано крестиком) до отправки — уходит в то же
    // сообщение сразу, без отдельного PATCH постфактум.
    const preview = linkPreview && linkPreviewUrlRef.current === firstUrl(value) ? linkPreview : null;
    // Очищаем поле после фиксации в IndexedDB; сетевые повторы уже принадлежат очереди.
    sendingTextRef.current = true;
    setSendingText(true);
    try {
      await realtime.send(chatId, {
        clientMsgId: randomUUID(),
        type: 1,
        payload: { alg: 'plaintext', data: preview ? { text: value, linkPreview: preview } : { text: value } },
        replyToId,
        roomId,
      }, sendSession);
      setText('');
      setReplyTo(null);
      clearLinkPreview();
      autoResize();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : t('sendFailed'));
    } finally {
      sendingTextRef.current = false;
      setSendingText(false);
    }
  };

  // Отправка контакта (как в KMP): обычное текстовое сообщение + entity type=contact.
  const sendContact = async (contact: UserBriefDto) => {
    setContactPickerOpen(false);
    const msg = buildContactReferenceMessage(contact);
    try {
      await realtime.send(chatId, {
        clientMsgId: randomUUID(),
        type: 1,
        payload: { alg: 'plaintext', data: msg.entities ? { text: msg.text, entities: msg.entities } : { text: msg.text } },
        replyToId: replyTo?.id ?? null,
        roomId,
      }, sendSession);
      setReplyTo(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : t('sendFailed'));
    }
  };

  // Показать ошибку загрузки медиа. Превышение лимита размера (413) — отдельный случай:
  // вместо обычного тоста предлагаем облачное хранилище (CloudLimitModal), т.к. простого
  // "файл слишком большой" пользователю недостаточно — нужна альтернатива для отправки.
  const mediaErrorTimerRef = useRef<number | null>(null);
  const showMediaError = (err: unknown) => {
    if (err instanceof ApiError && err.status === 413) {
      setCloudLimitMessage(err.message);
      return;
    }
    setMediaError(err instanceof Error ? err.message : t('fileError'));
    if (mediaErrorTimerRef.current) clearTimeout(mediaErrorTimerRef.current);
    mediaErrorTimerRef.current = window.setTimeout(() => setMediaError(''), 6000);
  };

  // getUserMedia (запись голосового/видео-сообщения) раньше падала совсем молча
  // (catch { /* доступ запрещён */ }) — пользователь нажимал на запись, и не происходило
  // вообще ничего: ни системного запроса разрешения (если браузер уже когда-то отказал —
  // повторно не спрашивает), ни любого объяснения (см. отчёт пользователя "не запрашивает
  // права ... на работу с камерой и микрофоном"). Та же схема имён DOMException, что и в
  // App.tsx showCallMediaWarning — для единообразия формулировок.
  const showDeviceError = (err: unknown, device: 'microphone' | 'camera') => {
    let reason: string;
    if (!window.isSecureContext) {
      reason = t('device.httpsRequired');
    } else {
      const name = err instanceof DOMException ? err.name : '';
      const reasonKey = name === 'NotAllowedError' ? 'device.permissionDenied'
        : name === 'NotFoundError' ? `device.${device}Missing`
          : name === 'NotReadableError' ? 'device.busy' : null;
      reason = reasonKey ? t(reasonKey) : name || t('device.unknown');
    }
    setMediaError(t('device.noAccess', { device: t(`device.${device}`), reason }));
    if (mediaErrorTimerRef.current) clearTimeout(mediaErrorTimerRef.current);
    mediaErrorTimerRef.current = window.setTimeout(() => setMediaError(''), 6000);
  };

  // Должно совпадать с бэкендом (application.yml: media.max-file-size = 500 MB — с запасом
  // под видео с телефона, которое сервер сам сожмёт в фоне, см. VideoCompressionService).
  // Проверяем ДО отправки на сервер: для сильно превышающих лимит файлов сервер не всегда
  // успевает ответить чистым 413 — Tomcat может оборвать соединение по Content-Length ещё до
  // того, как тело дошло, и fetch() в браузере падает сетевой ошибкой "Failed to fetch" вместо
  // ApiException(413), которую ловит showMediaError. Локальная проверка размера надёжнее
  // и мгновенная — не нужно гонять файл по сети, чтобы узнать то, что уже известно на клиенте.
  const MAX_FILE_SIZE = 500 * 1024 * 1024;
  // Максимальная длительность записи видео-кружка, сек (см. запрос пользователя — поставили
  // 3 минуты): по истечении запись останавливается и отправляется автоматически. Без лимита
  // можно было случайно записать кружок на десятки минут и упереться в 500 МБ лимит загрузки.
  const VIDEO_RECORD_MAX_SECONDS = 3 * 60;

  // Общий фильтр для "Прикрепить" и drag-and-drop: слишком большие файлы сразу уходят в
  // предложение облака (см. CloudLimitModal), а не в предпросмотр — остальные, если есть,
  // отправляются в обычный диалог подписи/отправки как раньше.
  const stageFiles = (files: File[], source: AttachmentSource = 'other') => {
    const tooBig = files.filter((f) => f.size > MAX_FILE_SIZE);
    const ok = files.filter((f) => f.size <= MAX_FILE_SIZE);
    if (tooBig.length > 0) {
      setCloudLimitMessage(
        tooBig.length === 1
          ? t('fileTooBigOne', { name: tooBig[0].name })
          : t('fileTooBigMany', { count: tooBig.length }),
      );
    }
    if (ok.length > 0) setPendingAttachments(ok.map((file) => ({ file, source })));
  };

  // Файлы из меню "Прикрепить" уходят в тот же диалог предпросмотра/подписи, что и
  // drag-and-drop (см. AttachPreviewModal) — не отправляем мгновенно, чтобы можно было
  // убрать лишнее и добавить общую подпись перед отправкой.
  const onAttach = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Закрываем меню только теперь, а не по клику на <label> — иначе React убирает
    // <input> из DOM ещё до того, как нативный диалог ОС успевает вернуть выбранные
    // файлы, и событие change срабатывает на уже отсоединённом узле (React его не видит,
    // делегированный обработчик молчит без единой ошибки). Из-за этого вложение реально
    // прикреплялось только через программную подстановку файла, а не через сам диалог.
    setAttachMenuOpen(false);
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    const source: AttachmentSource = e.currentTarget.dataset.attachmentSource === 'photo' ? 'photo' : 'document';
    stageFiles(files, source);
  };

  // Отправка группы вложений (drag-and-drop или несколько файлов разом из "Прикрепить") —
  // грузим их параллельно и уходит ОДНО сообщение с массивом media[] и общей подписью,
  // а не сообщение на файл (см. ChatView.renderMedia/media-group на стороне рендера).
  // items — StagedAttachment[] (файл + опциональный trim от VideoTrimEditor, см. AttachPreviewModal).
  const sendAttachments = async (items: StagedAttachment[], caption: string): Promise<string | null> => {
    // Сжимается только выбранное через «Фото»; «Документ», drop и paste сохраняют исходные байты.
    const preparedItems = await Promise.all(items.map(async (item) => ({
      ...item,
      file: item.source === 'photo' ? await compressPhotoForUpload(item.file) : item.file,
    })));
    const files = preparedItems.map((it) => it.file);
    const singleKind = files.length === 1 ? classifyFile(files[0]) : null;
    const label = files.length > 1
      ? t('upload.multipleFiles', { count: files.length })
      : singleKind === 'video' ? t('upload.video')
      : singleKind === 'image' ? t('upload.image')
      : singleKind === 'audio' ? t('upload.audio')
      : t('upload.file');
    const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
    const authorization = await requestTransferAuthorization({ direction: 'upload', label, sizeBytes: totalBytes });
    if (!authorization.allowed) return authorization.reason ?? t('media.wifiRequired');

    setPendingAttachments(null);
    // Каждая запущенная загрузка — отдельный элемент в pendingUploads (см. объявление state):
    // параллельные загрузки больше не перезаписывают друг друга.
    const uploadId = ++pendingUploadIdRef.current;
    setPendingUploads((cur) => [...cur, { id: uploadId, count: files.length, label: `${label}…`, progress: 0 }]);
    // Прогресс — по сумме фактических байт после обработки фото, а не среднее по проценту каждого.
    const totalProgressBytes = totalBytes || 1;
    const loadedByIndex = new Array(files.length).fill(0);
    let lastProgressUpdate = 0;
    const reportProgress = () => {
      const now = Date.now();
      if (now - lastProgressUpdate < 100) return;
      lastProgressUpdate = now;
      const loaded = loadedByIndex.reduce((sum, v) => sum + v, 0);
      const pct = Math.min(100, Math.round((loaded / totalProgressBytes) * 100));
      setPendingUploads((cur) => cur.map((u) => (u.id === uploadId ? { ...u, progress: pct } : u)));
    };
    try {
      const uploaded = await Promise.all(
        preparedItems.map((it, i) =>
          uploadMediaWithProgress(
            it.file,
            undefined,
            (loadedBytes) => {
              loadedByIndex[i] = loadedBytes;
              reportProgress();
            },
            it.trim,
          ),
        ),
      );
      const media: MediaItem[] = uploaded.map((m, i) => ({
        fileId: m.id,
        kind: classifyFile(files[i]),
        fileName: files[i].name,
        sizeBytes: files[i].size,
      }));
      // type — грубая подсказка (совместимость со списком чатов и т.п.), источник истины
      // для рендера самого сообщения — kind на каждом элементе media, не этот type.
      const type = media.length === 1 && media[0].kind === 'video' ? 3
        : media.length === 1 && media[0].kind === 'audio' ? 4
        : 2;
      await realtime.send(chatId, {
        clientMsgId: randomUUID(),
        type,
        payload: { alg: 'plaintext', data: caption ? { media, text: caption } : { media } },
        roomId,
      }, sendSession);
    } catch (err) {
      showMediaError(err);
    } finally {
      setPendingUploads((cur) => cur.filter((u) => u.id !== uploadId));
    }
    return null;
  };

  // Drag-and-drop файлов прямо на ленту сообщений — тот же диалог предпросмотра/подписи,
  // что и при выборе через "Прикрепить" (см. AttachPreviewModal). Считаем глубину
  // вложенности enter/leave (dragDepthRef), а не полагаемся на один флаг: dragleave
  // срабатывает при заходе на любого потомка внутри .chat-body, иначе подсветка дропзоны
  // мигала бы при перемещении курсора над самими сообщениями.
  const onChatDragEnter = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setDragActive(true);
  };
  const onChatDragOver = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
  };
  const onChatDragLeave = (e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setDragActive(false);
  };
  const onChatDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepthRef.current = 0;
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) stageFiles(files, 'other');
  };

  const sendRecordedUpload = async (recording: PendingRecordedUpload) => {
    if (activeRecordedUploadsRef.current.has(recording.id)) return;
    activeRecordedUploadsRef.current.add(recording.id);
    setPendingRecordedUploads((current) => {
      const existing = current.find((item) => item.id === recording.id);
      return existing
        ? current.map((item) => item.id === recording.id ? { ...item, status: 'sending', error: undefined } : item)
        : [...current, { ...recording, status: 'sending', error: undefined }];
    });
    let uploadedFileId = recording.uploadedFileId;
    let clientMsgId = recording.clientMsgId;
    try {
      if (!uploadedFileId) {
        const authorization = await requestTransferAuthorization({
          direction: 'upload',
          label: recording.kind === 'voice' ? t('upload.voiceMessage') : t('upload.videoMessage'),
          sizeBytes: recording.blob.size,
        });
        if (!authorization.allowed) {
          const reason = authorization.reason ?? t('media.wifiRequired');
          setPendingRecordedUploads((current) => current.map((item) => item.id === recording.id
            ? { ...item, status: 'waiting', error: reason } : item));
          showTransferNotice(reason);
          return;
        }
        const media = await uploadMedia(recording.blob, recording.name);
        uploadedFileId = media.id;
        setPendingRecordedUploads((current) => current.map((item) => item.id === recording.id
          ? { ...item, uploadedFileId, status: 'sending' } : item));
      }
      if (!uploadedFileId) throw new Error(t('upload.notCompleted'));
      clientMsgId = clientMsgId ?? randomUUID();
      setPendingRecordedUploads((current) => current.map((item) => item.id === recording.id
        ? { ...item, uploadedFileId, clientMsgId, status: 'sending' } : item));
      await realtime.send(chatId, {
        clientMsgId,
        type: recording.kind === 'voice' ? 4 : 3,
        payload: { alg: 'plaintext', data: { media: [{ fileId: uploadedFileId, kind: recording.kind === 'voice' ? 'audio' : 'video' }] } },
        roomId,
      }, sendSession);
      setPendingRecordedUploads((current) => current.filter((item) => item.id !== recording.id));
    } catch (err) {
      showMediaError(err);
      setPendingRecordedUploads((current) => current.map((item) => item.id === recording.id
        ? { ...item, uploadedFileId, clientMsgId, status: 'error', error: t('uploadFailed') } : item));
    } finally {
      activeRecordedUploadsRef.current.delete(recording.id);
    }
  };

  // Запись аудиосообщения (MediaRecorder)
  const previewRecording = (recorder: MediaRecorder | null) => {
    if (recorder?.state !== 'recording') return;
    previewRecordingRef.current = true;
    recorder.stop();
  };
  const toggleMic = async (hold?: VoiceHold) => {
    if (recording) {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
      return;
    }
    if (micStartingRef.current) return;
    micStartingRef.current = true;
    const attempt = ++micAttemptRef.current;
    let acquiredStream: MediaStream | undefined;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      acquiredStream = stream;
      // Разрешение может прийти после отпускания пальца, отмены или ухода из чата.
      if (attempt !== micAttemptRef.current || hold?.result === 'cancel' || hold?.result === 'send') {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data.size) chunksRef.current.push(e.data);
      };

      // "Бегущая строка" уровня звука — отдельный AnalyserNode поверх того же потока, не мешает
      // самой записи. Столбик добавляется не на каждый кадр (иначе бежала бы слишком быстро и
      // прыгала), а раз в ~90мс, ограниченным окном — старые слева выпадают.
      const WAVE_BARS = 40;
      const WAVE_INTERVAL_MS = 90;
      const ctx = new AudioContext();
      micAudioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const freqData = new Uint8Array(analyser.frequencyBinCount);
      let lastSampleAt = 0;
      let lastElapsedAt = 0;
      setWaveLevels([]);
      setVoiceElapsedMs(0);
      const startedAt = performance.now();
      const tick = (now: number) => {
        analyser.getByteFrequencyData(freqData);
        // Таймер записи обновляем реже уровня звука: 4 раза в секунду достаточно для «0:07».
        if (now - lastElapsedAt >= 250) {
          lastElapsedAt = now;
          setVoiceElapsedMs(now - startedAt);
        }
        if (now - lastSampleAt >= WAVE_INTERVAL_MS) {
          lastSampleAt = now;
          let sum = 0;
          for (const v of freqData) sum += v * v;
          const level = Math.min(1, Math.sqrt(sum / freqData.length) / 100);
          setWaveLevels((prev) => {
            const next = prev.length >= WAVE_BARS ? prev.slice(prev.length - WAVE_BARS + 1) : prev.slice();
            next.push(level);
            return next;
          });
        }
        micRafRef.current = requestAnimationFrame(tick);
      };
      micRafRef.current = requestAnimationFrame(tick);

      rec.onstop = async () => {
        if (voiceLimitTimerRef.current != null) {
          window.clearTimeout(voiceLimitTimerRef.current);
          voiceLimitTimerRef.current = null;
        }
        stream.getTracks().forEach((t) => t.stop());
        if (micRafRef.current != null) cancelAnimationFrame(micRafRef.current);
        micRafRef.current = null;
        micAudioCtxRef.current?.close().catch(() => {});
        micAudioCtxRef.current = null;
        setWaveLevels([]);
        setRecording(false);
        if (recordingCancelledRef.current) {
          recordingCancelledRef.current = false;
          return;
        }
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size > 0) {
          const prepared: PendingRecordedUpload = {
            id: ++pendingRecordedIdRef.current,
            blob,
            name: 'voice.webm',
            mimeType: blob.type || 'audio/webm',
            kind: 'voice',
            status: 'waiting',
          };
          if (previewRecordingRef.current) {
            previewRecordingRef.current = false;
            setRecordedPreview(prepared);
          } else void sendRecordedUpload(prepared);
        }
      };
      recorderRef.current = rec;
      recordingCancelledRef.current = false;
      previewRecordingRef.current = false;
      rec.start();
      // Лимит 10 минут: запись останавливается сама и уходит обычным сообщением — как в KMP
      // (отзыв #451). Таймер живёт до onstop, там же снимается при любой остановке.
      if (voiceLimitTimerRef.current != null) window.clearTimeout(voiceLimitTimerRef.current);
      voiceLimitTimerRef.current = window.setTimeout(() => {
        voiceLimitTimerRef.current = null;
        if (rec.state === 'recording') rec.stop();
      }, VOICE_MAX_MS);
      setVoiceLocked(!hold || hold.result === 'lock');
      setRecording(true);
    } catch (err) {
      acquiredStream?.getTracks().forEach((track) => track.stop());
      if (attempt === micAttemptRef.current && hold?.result !== 'cancel' && hold?.result !== 'send') showDeviceError(err, 'microphone');
    } finally {
      micStartingRef.current = false;
    }
  };

  // Предпросмотр своей камеры при записи: <video> монтируется только когда videoRecording
  // становится true, поэтому srcObject проставляем ПОСЛЕ монтирования (эффект, не setTimeout —
  // иначе рефа ещё нет и превью остаётся чёрным).
  useEffect(() => {
    if (videoRecording && videoPreviewRef.current && videoStreamRef.current) {
      videoPreviewRef.current.srcObject = videoStreamRef.current;
    }
  }, [videoRecording]);

  // Запись видео-сообщения (MediaRecorder, фронтальная камера + микрофон)
  const toggleVideoRec = async (hold?: VoiceHold) => {
    if (videoRecording) {
      if (videoRecorderRef.current?.state === 'recording') videoRecorderRef.current.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: true });
      if (hold?.result === 'cancel' || hold?.result === 'send') {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
        ? 'video/webm;codecs=vp8,opus'
        : MediaRecorder.isTypeSupported('video/mp4') ? 'video/mp4' : '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      videoChunksRef.current = [];
      videoStreamRef.current = stream;
      rec.ondataavailable = (e) => {
        if (e.data.size) videoChunksRef.current.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        videoStreamRef.current = null;
        setVideoRecording(false);
        if (videoRecordingCancelledRef.current) {
          videoRecordingCancelledRef.current = false;
          return;
        }
        const ext = mime.includes('mp4') ? 'mp4' : 'webm';
        const blob = new Blob(videoChunksRef.current, { type: mime || 'video/webm' });
        if (blob.size > 0) {
          const prepared: PendingRecordedUpload = {
            id: ++pendingRecordedIdRef.current,
            blob,
            name: `video.${ext}`,
            mimeType: blob.type || 'video/webm',
            kind: 'video',
            status: 'waiting',
          };
          if (previewRecordingRef.current) {
            previewRecordingRef.current = false;
            setRecordedPreview(prepared);
          } else void sendRecordedUpload(prepared);
        }
      };
      videoRecorderRef.current = rec;
      videoRecordingCancelledRef.current = false;
      previewRecordingRef.current = false;
      rec.start();
      setVoiceLocked(!hold || hold.result === 'lock');
      setVideoRecording(true); // <video> смонтируется — srcObject проставит эффект ниже
      // Авто-остановка по лимиту (см. VIDEO_RECORD_MAX_SECONDS): onstop уже отправит сообщение.
      // Проверка state — если пользователь сам остановил запись раньше, повторно не останавливаем.
      window.setTimeout(() => {
        if (rec.state === 'recording') rec.stop();
      }, VIDEO_RECORD_MAX_SECONDS * 1000);
    } catch (err) {
      showDeviceError(err, 'camera');
    }
  };

  const cancelVideoRec = () => {
    videoRecordingCancelledRef.current = true;
    if (videoRecorderRef.current?.state === 'recording') videoRecorderRef.current.stop();
    setVideoRecording(false);
  };

  const LONG_PRESS_MS = 380;
  const clearLongPressTimer = () => {
    if (longPressTimerRef.current != null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };
  const handlePressStart = (e: React.PointerEvent<HTMLButtonElement>) => {
    longPressFiredRef.current = false;
    if (e.button !== 0 || !e.isPrimary) return;
    if (e.pointerType === 'touch' && !text.trim() && !recording && !videoRecording && !recordedPreview) {
      if (micStartingRef.current) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      clearLongPressTimer();
      touchRecordingStartedRef.current = false;
      voiceHoldRef.current = { gesture: new VoiceHold(), x: e.clientX, y: e.clientY, pointerId: e.pointerId };
      setVoiceLocked(false);
      const hold = voiceHoldRef.current;
      longPressTimerRef.current = window.setTimeout(() => {
        // Ignore a timer that outlived its pointer gesture (release or cancel).
        if (voiceHoldRef.current !== hold || hold.gesture.result === 'cancel') return;
        longPressFiredRef.current = true;
        touchRecordingStartedRef.current = true;
        if (armedRecordType === 'video') void toggleVideoRec(hold.gesture);
        else void toggleMic(hold.gesture);
      }, 220);
      return;
    }
    if (text.trim() || recording || videoRecording || recordedPreview) return;
    longPressFiredRef.current = false;
    clearLongPressTimer();
    longPressTimerRef.current = window.setTimeout(() => {
      longPressFiredRef.current = true;
      if (armedRecordType === 'video') void toggleVideoRec();
      else void toggleMic();
    }, LONG_PRESS_MS);
  };
  const handlePressMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const hold = voiceHoldRef.current;
    if (!hold || hold.pointerId !== e.pointerId) return;
    const action = hold.gesture.move(voiceHoldAction(hold, { x: e.clientX, y: e.clientY }));
    if (action === 'cancel') {
      clearLongPressTimer();
      touchRecordingStartedRef.current = true;
      longPressFiredRef.current = true;
      voiceHoldRef.current = null;
      if (armedRecordType === 'video') cancelVideoRec(); else cancelRecording();
      return;
    }
    if (!touchRecordingStartedRef.current) return;
    if (action === 'lock') setVoiceLocked(true);
  };
  const handlePressEnd = (e: React.PointerEvent<HTMLButtonElement>) => {
    clearLongPressTimer();
    const hold = voiceHoldRef.current;
    if (!hold || hold.pointerId !== e.pointerId) return;
    voiceHoldRef.current = null;
    if (!touchRecordingStartedRef.current) {
      // Короткое нажатие — переключение типа записи (микрофон/видео), запись не стартует.
      touchRecordingStartedRef.current = false;
      armRecordType(armedRecordType === 'voice' ? 'video' : 'voice');
      longPressFiredRef.current = true;
      return;
    }
    touchRecordingStartedRef.current = false;
    const action = hold.gesture.release(e.type === 'pointercancel' || e.type === 'lostpointercapture');
    if (action === 'cancel') {
      if (armedRecordType === 'video') cancelVideoRec(); else cancelRecording();
    }
    const recorder = armedRecordType === 'video' ? videoRecorderRef.current : recorderRef.current;
    if (action === 'send' && recorder?.state === 'recording') recorder.stop();
  };
  const handleMainClick = () => {
    // Долгое нажатие уже открыло выбор типа сообщения — этот клик лишь завершает
    // то же самое нажатие мышью/пальцем и не должен запускать отправку/запись повторно.
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    if (recordedPreview) {
      const prepared = recordedPreview;
      setRecordedPreview(null);
      void sendRecordedUpload(prepared);
      return;
    }

    if (recording) {
      if (recorderRef.current && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
      return;
    }
    if (videoRecording) {
      videoRecorderRef.current?.stop();
      return;
    }
    if (text.trim()) {
      sendText();
      return;
    }
    // Тап по основной кнопке переключает тип записи (микрофон/видео); запись — только удержанием.
    armRecordType(armedRecordType === 'voice' ? 'video' : 'voice');
  };

  // Выбор типа записи по умолчанию из меню — только переключает armedRecordType и сохраняет
  // его на сервере (см. api/user.ts setDefaultRecordType), саму запись не запускает.
  const armRecordType = (type: 'voice' | 'video') => {
    longPressFiredRef.current = false;
    if (type === armedRecordType) return;
    setArmedRecordType(type);
    apiSetDefaultRecordType(type).catch(() => {});
  };

  // В группе у typing-события есть только user_id (см. ChatMessageWs.typing на бэкенде) —
  // имя ищем в уже загруженных сообщениях по отправителю, best-effort, без похода на сервер.
  const typingLabel = (() => {
    const ids = typingUserIds.filter((id) => id !== me?.id);
    if (ids.length === 0) return null;
    if (chat?.type !== 2) return t('typing.one');
    if (ids.length > 1) return t('typing.multiple');
    const name = messages.find((m) => m.senderId === ids[0])?.senderName;
    return t('typing.named', { name: name || t('typing.unknown') });
  })();

  return (
    <>
      {selectMode ? (
        <div className="chat-header select-header">
          <button className="back-btn" title={t('selection.cancel')} onClick={exitSelectMode}>
            <span style={{ fontSize: 20 }}>✕</span>
          </button>
          <div className="title" style={{ flex: 1 }}>{t('selection.count', { count: selectedIds.size })}</div>
          {selectedIds.size === 1 && (
            <button
              className="call-mini"
              title={t('menu.reply')}
              onClick={() => {
                const [id] = selectedIds;
                exitSelectMode();
                handleReply(id);
              }}
            >
              <img src="/reply.svg" alt={t('menu.reply')} />
            </button>
          )}
          <button className="call-mini" title={t('menu.forward')} onClick={() => setForwardIds([...selectedIds])}>
            <img src="/forward.svg" alt={t('menu.forward')} />
          </button>
          {messages.some((message) => selectedIds.has(message.id) && !!copyableMessageText(message)) && (
            <button className="call-mini" title={t('menu.copy')} onClick={copySelected}>
              <img src="/document.svg" alt={t('menu.copy')} />
            </button>
          )}
        </div>
      ) : (
        <div className="chat-header adaptive-header" ref={headerRef}>
          <button className="back-btn" title={t('header.back')} onClick={onBack}>
            <img src="/back.svg" alt={t('header.back')} />
          </button>
          <div className="chat-header-info" onClick={() => setChatInfoOpen(true)} title={t('header.chatInfo')}>
            {activeRoom ? (
              <RoomAvatar avatarFileId={activeRoom.avatarFileId} name={activeRoom.name} size={36} />
            ) : (
              <Avatar
                userId={chat?.otherUserId}
                chatId={chat && (chat.type === 2 || !chat.otherUserId) ? chat.id : null}
                name={chat?.title || '#'}
                size={36}
                version={chat?.avatarFileId}
              />
            )}
            <div className="chat-header-texts">
              <div className="title">{chat?.title || t('chats:fallback.chat')}{roomName ? ` · ${roomName}` : ''}</div>
              {!wsOnline ? (
                <ConnectingBadge className="chat-header-status" />
              ) : typingLabel ? (
                <div className="chat-header-status">{typingLabel}</div>
              ) : (
                chat?.type === 1 && presence && <div className="chat-header-status">{formatPresence(presence)}</div>
              )}
            </div>
          </div>
          {/* Сначала освобождаем место за счёт видео, затем аудио. */}
          {chat && (chat.type === 2 || chat.otherUserId) && (
            <>
              <button hidden={callCount < 1} className="call-mini" title={t('header.audioCall')} disabled={callActive} onClick={() => onStartCall(false)}>
                <img src="/audio-call.svg" alt={t('header.audioCall')} />
              </button>
              <button hidden={callCount < 2} className="call-mini" title={t('header.videoCall')} disabled={callActive} onClick={() => onStartCall(true)}>
                <img src="/video-call.svg" alt={t('header.videoCall')} />
              </button>
            </>
          )}
          <button type="button" className="call-mini header-select" title={t('header.select')} onClick={() => setSelectMode(true)}>
            <img src="/select.svg" alt={t('header.select')} />
          </button>
          <button className="call-mini header-search" title={t('header.search')} onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}>
            <img src="/search.svg" alt={t('header.search')} />
          </button>
          <button className="call-mini header-more-btn" title={t('header.more')} onClick={() => { setHeaderNotificationsOpen(false); setHeaderMenuOpen((v) => !v); }}>
            ⋮
          </button>
          {headerMenuOpen && (
            <>
              <div className="header-more-backdrop" onClick={() => { setHeaderNotificationsOpen(false); setHeaderMenuOpen(false); }} />
              <div className="header-more-menu">
                {(canManageSupportNotifications(chat, me?.role) || (chat && !chat.isNotes && !chat.isSupport)) && (
                  <>
                    <button
                      type="button"
                      className="ctx-item"
                      aria-expanded={headerNotificationsOpen}
                      onClick={() => setHeaderNotificationsOpen((v) => !v)}
                    >
                      <NotificationIcon variant={chat && isChatMutedAt(chat) ? 'off' : 'on'} />
                      <span style={{ flex: 1, textAlign: 'left' }}>{t('header.notifications')}</span>
                      <span aria-hidden="true" style={{ color: 'var(--muted)', fontSize: 12 }}>{headerNotificationsOpen ? '▾' : '▸'}</span>
                    </button>
                    {headerNotificationsOpen && (
                      <section className="header-submenu">
                        {canManageSupportNotifications(chat, me?.role) && <SupportNotificationSettings key={sendSession} />}
                        {chat && !chat.isNotes && !chat.isSupport && (
                          isChatMutedAt(chat) ? (
                            <button type="button" className="ctx-item" disabled={muteBusy} onClick={() => void changeChatMute('off')}>
                              <NotificationIcon variant="on" /> {t('header.unmute')}
                            </button>
                          ) : (
                            <>
                              <button type="button" className="ctx-item" disabled={muteBusy} onClick={() => void changeChatMute('hour')}>
                                <NotificationIcon variant="timer" /> {t('header.mute1h')}
                              </button>
                              <button type="button" className="ctx-item" disabled={muteBusy} onClick={() => void changeChatMute('eight_hours')}>
                                <NotificationIcon variant="timer" /> {t('header.mute8h')}
                              </button>
                              <button type="button" className="ctx-item" disabled={muteBusy} onClick={() => void changeChatMute('twenty_hours')}>
                                <NotificationIcon variant="timer" /> {t('header.mute20h')}
                              </button>
                              <button type="button" className="ctx-item" disabled={muteBusy} onClick={() => void changeChatMute('day')}>
                                <NotificationIcon variant="timer" /> {t('header.mute1d')}
                              </button>
                              <button type="button" className="ctx-item" disabled={muteBusy} onClick={() => void changeChatMute('three_days')}>
                                <NotificationIcon variant="timer" /> {t('header.mute3d')}
                              </button>
                              <button type="button" className="ctx-item" disabled={muteBusy} onClick={() => void changeChatMute('seven_days')}>
                                <NotificationIcon variant="timer" /> {t('header.mute7d')}
                              </button>
                              <button type="button" className="ctx-item" disabled={muteBusy} onClick={() => void changeChatMute('thirty_days')}>
                                <NotificationIcon variant="timer" /> {t('header.mute30d')}
                              </button>
                              <button type="button" className="ctx-item" disabled={muteBusy} onClick={() => void changeChatMute('forever')}>
                                <NotificationIcon variant="off" /> {t('header.muteForever')}
                              </button>
                              <button
                                type="button"
                                className="ctx-item"
                                disabled={muteBusy}
                                onClick={() => {
                                  setCustomMuteOpen((open) => {
                                    if (!open) setCustomMuteDateTime(localDateTimeValue(new Date(Date.now() + 60 * 60 * 1000)));
                                    return !open;
                                  });
                                }}
                              >
                                <NotificationIcon variant="timer" /> {t('header.muteCustom')}
                              </button>
                              {customMuteOpen && (
                                <div className="custom-mute-editor">
                                  <label htmlFor="chat-mute-until">{t('header.muteUntil')}</label>
                                  <input
                                    id="chat-mute-until"
                                    type="datetime-local"
                                    min={localDateTimeValue(new Date())}
                                    value={customMuteDateTime}
                                    onChange={(event) => setCustomMuteDateTime(event.target.value)}
                                  />
                                  <button
                                    type="button"
                                    className="btn"
                                    disabled={muteBusy || !Number.isFinite(Date.parse(customMuteDateTime)) || Date.parse(customMuteDateTime) <= Date.now()}
                                    onClick={() => {
                                      const timestamp = Date.parse(customMuteDateTime);
                                      if (Number.isFinite(timestamp) && timestamp > Date.now()) {
                                        void changeChatMute('custom', new Date(timestamp).toISOString());
                                        setCustomMuteOpen(false);
                                      }
                                    }}
                                  >
                                    {t('header.muteApply')}
                                  </button>
                                </div>
                              )}
                            </>
                          )
                        )}
                      </section>
                    )}
                  </>
                )}
                <button
                  type="button"
                  className="ctx-item"
                  onClick={() => {
                    setHeaderMenuOpen(false);
                    setSelectMode(true);
                  }}
                >
                  <img src="/select.svg" alt="" /> {t('header.select')}
                </button>
                <button
                  type="button"
                  className="ctx-item"
                  onClick={() => {
                    setHeaderMenuOpen(false);
                    if (searchOpen) closeSearch();
                    else setSearchOpen(true);
                  }}
                >
                  <img src="/search.svg" alt="" /> {t('header.search')}
                </button>
                {chat && (chat.type === 2 || chat.otherUserId) && callCount < 1 && (
                  <button type="button" className="ctx-item" disabled={callActive} onClick={() => {
                    setHeaderMenuOpen(false);
                    onStartCall(false);
                  }}>
                    <img src="/audio-call.svg" alt="" /> {t('header.audioCall')}
                  </button>
                )}
                {chat && (chat.type === 2 || chat.otherUserId) && callCount < 2 && (
                  <button
                    type="button"
                    className="ctx-item"
                    disabled={callActive}
                    onClick={() => {
                      setHeaderMenuOpen(false);
                      onStartCall(true);
                    }}
                  >
                    <img src="/video-call.svg" alt="" /> {t('header.videoCall')}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {currentPinned && (
        <div className="pinned-banner" onClick={onPinnedBannerClick}>
          <img src="/pin.svg" alt="" className="pinned-banner-icon" />
          <div className="pinned-banner-body">
            <div className="pinned-banner-counter">
              {allPinned.length > 1 ? t('pinned.count', { current: (pinnedBannerIndex % allPinned.length) + 1, total: allPinned.length }) : t('pinned.single')}
              {currentPinned.scope === 'me' ? t('pinned.selfTag') : ''}
            </div>
            <div className="pinned-banner-text">{messageCopyText(currentPinned.msg)}</div>
          </div>
          <button
            type="button"
            className="pinned-banner-close"
            title={t('pinned.unpin')}
            onClick={(e) => {
              e.stopPropagation();
              onUnpinCurrent();
            }}
          >
            ✕
          </button>
        </div>
      )}

      {searchOpen && (
        <div className="search-panel">
          <div className="search-input-row">
            <i className="search-input-icon">
              <img src="/search.svg" alt="" />
            </i>
            <input
              className="search-input"
              placeholder={t('search.placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') goToNextResult();
                if (e.key === 'Escape') closeSearch();
              }}
              autoFocus
            />
            {searchResults.length > 0 && (
              <span className="search-counter">
                {(activeResultIndex ?? 0) + 1} / {searchResults.length}
              </span>
            )}
            <button
              type="button"
              className="search-nav-btn"
              title={t('search.previous')}
              disabled={!searchResults.length}
              onClick={goToPrevResult}
            >
              ↑
            </button>
            <button
              type="button"
              className="search-nav-btn"
              title={t('search.next')}
              disabled={!searchResults.length}
              onClick={goToNextResult}
            >
              ↓
            </button>
            <button type="button" className="search-nav-btn" title={t('search.close')} onClick={closeSearch}>
              ✕
            </button>
          </div>
          {searchQuery.trim() && (
            <SearchResultsPanel>
              {searchResults.map((r, i) => {
                const mine = r.senderId === me?.id;
                const label = mine ? t('reply.self') : r.senderName || t('reply.senderFallback');
                return (
                  <div
                    key={r.id}
                    className={'search-result' + (i === activeResultIndex ? ' active' : '')}
                    onClick={() => jumpToResult(i)}
                  >
                    <Avatar userId={r.senderId} name={label} size={40} />
                    <div className="search-result-body">
                      <div className="search-result-top">
                        {isGroup && <span className="search-result-name">{label}</span>}
                        <span className="search-time">
                          {new Date(r.createdAt).toLocaleDateString(intlLocale(i18n.resolvedLanguage), { day: '2-digit', month: 'short' })}
                        </span>
                      </div>
                      <div className="search-text">{highlightMatch(messageCopyText(r), searchQuery.trim())}</div>
                    </div>
                  </div>
                );
              })}
              {searchLoading && <div className="search-status">{t('search.searching')}</div>}
              {searchError && <div className="search-status" role="alert">{searchError}</div>}
              {!searchLoading && !searchError && searchResults.length === 0 && (
                <div className="search-status">{t('search.empty')}</div>
              )}
            </SearchResultsPanel>
          )}
        </div>
      )}

      <div
        className="chat-body"
        onDragEnter={onChatDragEnter}
        onDragOver={onChatDragOver}
        onDragLeave={onChatDragLeave}
        onDrop={onChatDrop}
      >
        {dragActive && (
          <div className="dropzone-overlay">
            <div className="dropzone-overlay-text">{t('dropFiles')}</div>
          </div>
        )}
        <div
          className="messages"
          ref={scrollerRef}
          onScroll={handleScroll}
          onClick={(e) => {
            if (!selectMode) return;
            const y = e.clientY;
            let targetId: number | null = null;
            // Сообщения идут в документе сверху вниз, поэтому как только верх элемента ушёл
            // ниже клика — дальнейшие тем более не подойдут, можно прервать скан раньше и не
            // гонять getBoundingClientRect (реflow) по всей, потенциально длинной, истории чата.
            for (const el of document.querySelectorAll<HTMLElement>('.messages-inner .msg')) {
              const r = el.getBoundingClientRect();
              if (r.top > y) break;
              if (y >= r.top && y <= r.bottom) {
                const id = Number((el.id || '').replace('msg-', ''));
                if (!Number.isNaN(id)) {
                  targetId = id;
                  break;
                }
              }
            }
            if (targetId != null) {
              handleMsgBodyClick(targetId);
            } else if (selectMode) {
              exitSelectMode();
            }
          }}
        >
        <div className="messages-inner" ref={messagesInnerRef}>
          {messages.map((m, i) => {
            const curDate = dateLabel(m.createdAt);
            const prevDate = i > 0 ? dateLabel(messages[i - 1].createdAt) : null;
            return (
              <Fragment key={m.id}>
                {curDate !== prevDate && (
                  <div className="day-sticky"><div className="day">{curDate}</div></div>
                )}
                <MessageBubble
                  msg={m}
                  me={me}
                  statuses={statuses}
                  onOpen={openLightbox}
                  highlight={highlightId === m.id}
                  onContextMenu={openContextMenu}
                  showSender={isGroup}
                  isGroup={isGroup}
                  repliedMsg={m.replyToId != null ? messagesById.get(m.replyToId) : undefined}
                  onJumpToReply={goToMessage}
                  selectMode={selectMode}
                  selected={selectedIds.has(m.id)}
                  ctxTarget={ctxMenu?.id === m.id}
                  onToggleSelect={toggleSelect}
                  onReply={handleReply}
                  onReact={reactToMessage}
                  transcript={transcripts.get(m.id)}
                   transcriptRevealed={revealedTranscripts.has(m.id)}
                   onRevealTranscript={() => setRevealedTranscripts((prev) => {
                     const next = new Set(prev);
                     if (next.has(m.id)) next.delete(m.id); else next.add(m.id);
                     return next;
                   })}
                  onTranscribe={runTranscribe}
                  forwardedFromLive={
                    m.payload.data?.forwardedFromUserId
                      ? forwardedNames.get(m.payload.data.forwardedFromUserId)
                      : undefined
                  }
                />
              </Fragment>
            );
          })}
          <OutboxMessages ownerId={me?.id} chatId={chatId} roomId={roomId} messages={messages} />
          {sendingText && <div className="msg me pending-upload-msg" role="status"><span aria-label={t('status.waiting')}>◷</span> {t('status.waiting')}</div>}
          {pendingUploads.map((u) => (
            <div key={u.id} className="msg me pending-upload-msg">
              <div className="pending-upload-msg-row">
                <span className="spinner" />
                {u.label}
                <span className="pending-upload-progress-pct">{u.progress}%</span>
              </div>
              <div className="pending-upload-progress-track">
                <div className="pending-upload-progress-fill" style={{ width: u.progress + '%' }} />
              </div>
            </div>
          ))}
          {pendingRecordedUploads.map((u) => (
            <div key={`recorded-${u.id}`} className="msg me pending-upload-msg">
              <div className="pending-upload-msg-row">
                <span>{u.status === 'sending' ? <span className="spinner" /> : null}</span>
                <span>{u.status === 'sending' ? t('upload.sending') : u.error || t('upload.recordingReady', { name: u.name })}</span>
                {u.status !== 'sending' && (
                  <>
                    <button type="button" className="link-btn" onClick={() => void sendRecordedUpload(u)}>{t('upload.retry')}</button>
                    <button type="button" className="link-btn" onClick={() => setPendingRecordedUploads((items) => items.filter((item) => item.id !== u.id))}>{t('upload.cancel')}</button>
                  </>
                )}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
        </div>
        {showGoDown && (
          <button
            className="go-down"
            title={unreadCount > 0 ? t('scroll.toUnread') : t('pinned.atEnd')}
            aria-label={unreadCount > 0 ? t('scroll.unreadCount', { count: unreadCount }) : t('pinned.atEnd')}
            onClick={handleGoDown}
          >
            ↓
            {unreadCount > 0 && <span className="go-down-count">{unreadCount}</span>}
          </button>
        )}
      </div>

      {mediaError && (
        <div className="recording-bar">
          <span>⚠ {mediaError}</span>
          <button className="editing-cancel" onClick={() => setMediaError('')}>✕</button>
        </div>
      )}

      {toast && <div className="chat-toast">{toast}</div>}

      {recordedPreview && <RecordedPreview blob={recordedPreview.blob} video={recordedPreview.kind === 'video'} onCancel={() => setRecordedPreview(null)} />}
      {videoRecording && (
        <div className="recording-bar">
          <span>{t('recording.video')}</span>
          {!voiceLocked && <span>{t('recording.holdHint')}</span>}
          <button className="editing-cancel" onClick={cancelVideoRec} aria-label={t('recording.cancel')}>✕</button>
          <button type="button" className="link-btn" disabled={!voiceLocked} onClick={() => previewRecording(videoRecorderRef.current)}>{t('recording.preview')}</button>
        </div>
      )}
      {videoRecording && <video ref={videoPreviewRef} muted autoPlay playsInline className="video-preview" />}

      {recording && (
        <div className="recording-bar">
          <span>{t('recording.voice')}</span>
          <span>{formatRecordingLimit(voiceElapsedMs)}</span>
          {!voiceLocked && <span>{t('recording.holdHint')}</span>}
          <div className="recording-wave" aria-hidden="true">
            {waveLevels.map((v, i) => (
              <span key={i} className="recording-wave-bar" style={{ height: `${Math.max(12, Math.round(v * 100))}%` }} />
            ))}
          </div>
          <button type="button" className="editing-cancel" onClick={cancelRecording}>{t('recording.cancel')}</button>
          {voiceLocked && <button type="button" className="link-btn" onClick={() => previewRecording(recorderRef.current)}>{t('recording.preview')}</button>}
        </div>
      )}

      {editingId != null && (
        <div className="editing-bar">
          <span>{t('editing')}</span>
          <button
            className="editing-cancel"
            onClick={() => {
              editAttemptRef.current++;
              setEditingId(null);
              setText(originalDraftRef.current);
            }}
          >
            ✕
          </button>
        </div>
      )}

      {replyTo && (
        <div className="editing-bar">
          <div className="reply-bar-content">
            <div className="reply-bar-name">↩ {replyTo.senderId === me?.id ? t('reply.self') : replyTo.senderName || t('reply.fallback')}</div>
            <div className="reply-bar-text">{messageCopyText(replyTo)}</div>
          </div>
          <button className="editing-cancel" onClick={() => setReplyTo(null)}>✕</button>
        </div>
      )}

      {linkPreview && (
        <div className="editing-bar link-preview-bar">
          {linkPreviewThumb ? (
            <img className="link-preview-bar-thumb" src={linkPreviewThumb} alt="" />
          ) : (
            <img className="link-preview-bar-icon" src="/link.svg" alt="" />
          )}
          <div className="reply-bar-content">
            <div className="reply-bar-name">{linkPreview.siteName || t('link')}</div>
            <div className="reply-bar-text">{linkPreview.title || linkPreview.description || linkPreview.url}</div>
          </div>
          <button className="editing-cancel" title={t('removePreview')} onClick={dismissLinkPreview}>✕</button>
        </div>
      )}

      {/* Отдельная плавающая плашка над композером, а не в стопке баннеров под шапкой (см.
          отчёт пользователя — с закреплённым сообщением одновременно стопка из двух карточек
          подряд под шапкой выглядела тяжело; закреплённое явно важнее и приклеено к шапке,
          этот — второстепенный, одноразовый вопрос, и ему тут самое место у поля ввода). */}
      {showContactPrompt && (
        <div className="contact-prompt-banner">
          <div className="contact-prompt-text">{t('contact.prompt', { name: chat?.title || t('contact.fallbackName') })}</div>
          <div className="contact-prompt-actions">
            <button type="button" className="contact-prompt-btn" disabled={contactPromptBusy} onClick={addContactFromPrompt}>
              {t('misc:chatViewAddContact')}
            </button>
            <button type="button" className="contact-prompt-btn ghost" onClick={ignoreContactPrompt}>
              {t('misc:chatViewIgnoreContact')}
            </button>
          </div>
        </div>
      )}

      <div className="composer">
        {/* Слева — смайлики (раньше были справа от textarea; см. отчёт пользователя "поменять
            местами смайлики и прикрепить файл"). Круглая кнопка, как у прежнего прикрепления. */}
        <div className="attach-wrap">
          <button type="button" className="attach-btn" title={t('emoji')} onClick={() => setEmojiOpen((v) => !v)}>
            <img src="/emoji.svg" alt={t('emoji')} />
          </button>
          {emojiOpen && (
            <Suspense fallback={null}>
              <EmojiPicker onBackspace={() => setText(backspaceInput(text, taRef.current))} onSelect={insertEmoji} onClose={() => setEmojiOpen(false)} />
            </Suspense>
          )}
        </div>
        <div className="ta-wrap">
          <textarea
            ref={taRef}
            // Обычный placeholder по умолчанию (см. отчёт пользователя "в строке сообщения по
            // умолчанию показывать [Сообщение]") — подсказка про голосовое через placeholder
            // раньше показывалась даже когда поле просто ещё не тронуто, что выглядело так,
            // будто набрать текст нельзя. Что кнопка отправки — микрофон, видно по самой иконке.
            placeholder={t('messagePlaceholder')}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              autoResize();
              onComposerInput(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendText();
              }
            }}
            onPaste={(e) => {
              // Скриншот/файл из буфера (Win+Shift+S, "Копировать изображение" в браузере,
              // файл из проводника) — попадает как DataTransferItem с kind='file', не как
              // текст. Тот же диалог предпросмотра/подписи, что у drag-and-drop (см. stageFiles).
              const files = Array.from(e.clipboardData?.items ?? [])
                .filter((it) => it.kind === 'file')
                .map((it) => it.getAsFile())
                .filter((f): f is File => f != null);
              if (files.length > 0) {
                e.preventDefault();
                stageFiles(files, 'other');
              }
            }}
            rows={1}
          />
          {/* Прикрепить — теперь справа от textarea (на месте прежнего смайлика), иконка — скрепка. */}
          <div className="ta-attach-wrap">
            <button type="button" className="attach-btn" title={t('attach')} onClick={() => setAttachMenuOpen((v) => !v)}>
              <img src="/attach.svg" alt={t('attach')} />
            </button>
            {attachMenuOpen && (
              <>
                <div className="menu-backdrop" onClick={() => setAttachMenuOpen(false)} />
                <div className="attach-menu">
                  <label className="attach-menu-item">
                    <img src="/photo.svg" alt="" /> {t('attachMenu.photo')}
                    <input type="file" accept="image/*" multiple data-attachment-source="photo" style={{ display: 'none' }} onChange={onAttach} />
                  </label>
                  <label className="attach-menu-item">
                    <img src="/video-call.svg" alt="" /> {t('attachMenu.video')}
                    <input type="file" accept="video/*" multiple style={{ display: 'none' }} onChange={onAttach} />
                  </label>
                  <label className="attach-menu-item">
                    <img src="/audio-file.svg" alt="" /> {t('attachMenu.music')}
                    <input type="file" accept="audio/*" multiple style={{ display: 'none' }} onChange={onAttach} />
                  </label>
                  <label className="attach-menu-item">
                    <img src="/document.svg" alt="" /> {t('attachMenu.document')}
                    <input type="file" multiple style={{ display: 'none' }} onChange={onAttach} />
                  </label>
                  <button type="button" className="attach-menu-item" onClick={() => { setAttachMenuOpen(false); setContactPickerOpen(true); }}>
                    <img src="/contact.svg" alt="" /> {t('attachMenu.contact')}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        <div className="send-btn-wrap" style={{ display: 'flex', alignItems: 'center' }}>
          <button
            className={
              'send-btn' +
              (recording || videoRecording ? ' recording' : '') +
              (!text.trim() && !recording && !videoRecording && !recordedPreview ? ' empty' : '')
            }
            title={
              recording || videoRecording
                ? t('recording.stopAndSend')
                : text.trim()
                  ? t('recording.send')
                  : armedRecordType === 'video'
                    ? t('recording.recordVideoHint')
                    : t('recording.recordVoiceHint')
            }
            // Сенсор: удержание, фиксация вверх, отмена влево; мышь: клик.
            onPointerDown={handlePressStart}
            onPointerMove={handlePressMove}
            onPointerUp={handlePressEnd}
            onPointerCancel={handlePressEnd}
            onLostPointerCapture={handlePressEnd}
            style={{ touchAction: 'none' }}
            onClick={handleMainClick}
          >
            {/* При пустом сообщении кнопка отражает armedRecordType (микрофон/камера — тип
                записи по умолчанию, см. rec-picker выше и отчёт пользователя "хранить в БД
                кнопку по умолчанию"), при тексте — обычная отправка; во время записи —
                отправка-стоп. */}
            {/* draggable=false — иначе браузер подхватывает <img> в нативный drag сразу же при
                малейшем движении курсора с зажатой кнопкой, обрывая жест долгого нажатия
                на середине — см. отчёт пользователя "иконка сразу пропадает при перемещении
                курсора". */}
            {!text.trim() && !recording && !videoRecording && !recordedPreview ? (
              armedRecordType === 'video' ? (
                <img src="/video-call.svg" alt={t('upload.videoMessage')} draggable={false} />
              ) : (
                <img src="/mic.svg" alt={t('upload.voiceMessage')} draggable={false} />
              )
            ) : (
              <img src="/send.svg" alt={t('send')} draggable={false} />
            )}
          </button>
        </div>
      </div>

      {contactPickerOpen && <ContactPickerModal onClose={() => setContactPickerOpen(false)} onPick={sendContact} />}
      {pendingAttachments && (
        <AttachPreviewModal
          files={pendingAttachments}
          onCancel={() => setPendingAttachments(null)}
          onSend={sendAttachments}
        />
      )}

      {cloudLimitMessage && (
        <CloudLimitModal message={cloudLimitMessage} onClose={() => setCloudLimitMessage(null)} />
      )}

      {lightbox && createPortal(
        <div
          className="lightbox"
          onClick={onLightboxClick}
          onPointerDown={onLightboxPointerDown}
          onPointerUp={onLightboxPointerUp}
        >
          <div
            className="lightbox-toolbar"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="lightbox-close"
              title={t('header.more')}
              onClick={() => setLightboxMenuOpen((v) => !v)}
            >
              ⋮
            </button>
            <button type="button" className="lightbox-close" title={t('lightbox.close')} onClick={closeLightbox}>✕</button>
            {lightboxMenuOpen && (
              <>
                <div className="lightbox-menu-backdrop" onClick={() => setLightboxMenuOpen(false)} />
                <div className="lightbox-menu">
                  {lightboxDownloadCurrent && (
                    <>
                      <button
                        type="button"
                        className="ctx-item"
                        disabled={!!albumDl && !albumDl.finished}
                        onClick={() => { void downloadAlbumItems([lightboxDownloadCurrent]); }}
                      >
                        <img src="/download-accent.svg" alt="" /> {t(lightboxDownloads.all.length > 1 ? 'lightbox.downloadThis' : 'menu.download')}
                      </button>
                      {lightboxDownloads.all.length > 1 && (
                        <button
                          type="button"
                          className="ctx-item"
                          disabled={!!albumDl && !albumDl.finished}
                          onClick={() => { void downloadAlbumItems(lightboxDownloads.all); }}
                        >
                          <img src="/download-accent.svg" alt="" /> {t('lightbox.downloadAll')}
                        </button>
                      )}
                    </>
                  )}
                  <button
                    type="button"
                    className="ctx-item"
                    onClick={() => {
                      const id = lightbox.messageId;
                      closeLightbox();
                      handleReply(id);
                    }}
                  >
                    <img src="/reply.svg" alt="" /> {t('menu.reply')}
                  </button>
                  <button
                    type="button"
                    className="ctx-item"
                    onClick={() => {
                      const id = lightbox.messageId;
                      closeLightbox();
                      setForwardIds([id]);
                    }}
                  >
                    <img src="/forward.svg" alt="" /> {t('menu.forward')}
                  </button>
                  {canDeleteMessage(lightbox.senderId) && (
                    <button
                      type="button"
                      className="ctx-item danger"
                      onClick={() => {
                        const id = lightbox.messageId;
                        closeLightbox();
                        handleDelete(id);
                      }}
                    >
                      <img src="/trash.svg" alt="" /> {t('menu.delete')}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          {/* На краях альбома скрываем стрелку в недоступном направлении. */}
          {mediaNavigationIndex(lightboxIndex, lightboxMediaList.length, -1, isAlbum) >= 0 && (
            <button
              type="button"
              className="lightbox-nav lightbox-nav-prev"
              title={t('lightbox.previous')}
              onClick={(e) => {
                e.stopPropagation();
                lightboxNav(-1);
              }}
            >
              ‹
            </button>
          )}
          {mediaNavigationIndex(lightboxIndex, lightboxMediaList.length, 1, isAlbum) >= 0 && (
            <button
              type="button"
              className="lightbox-nav lightbox-nav-next"
              title={t('lightbox.next')}
              onClick={(e) => {
                e.stopPropagation();
                lightboxNav(1);
              }}
            >
              ›
            </button>
          )}
          {isAlbum && lightboxIndex >= 0 && (
            <div
              className="lightbox-counter"
              onClick={(e) => e.stopPropagation()}
            >
              {lightboxIndex + 1}/{lightboxMediaList.length}
            </div>
          )}
          {albumDl && (
            <div className="lightbox-dl-status" role="status" onClick={(e) => e.stopPropagation()}>
              {albumDl.finished
                ? t('lightbox.saved', { saved: albumDl.saved, total: albumDl.total })
                : t('lightbox.downloading', { done: albumDl.done, total: albumDl.total })}
              {!albumDl.finished && (
                <div className="lightbox-dl-bar"><div style={{ width: `${albumDl.total ? (albumDl.done / albumDl.total) * 100 : 0}%` }} /></div>
              )}
            </div>
          )}
          {lightbox.kind === 'video' ? (
            <FullscreenVideo
              key={lightbox.fileId}
              src={lightbox.src}
              poster={lightbox.posterUrl}
              startTime={lightbox.startTime}
              onTime={time => { lightboxTimeRef.current = time; }}
            />
          ) : (
            <img
              src={lightbox.src}
              alt=""
              onClick={(e) => e.stopPropagation()}
              onPointerDown={onLightboxPointerDown}
              onPointerUp={onLightboxPointerUp}
            />
          )}
        </div>,
        document.body,
      )}

      {chatInfoOpen && (
        <ErrorBoundary>
        <ChatInfoModal
          chatId={chatId}
          me={me}
          roomId={roomId}
          onClose={() => setChatInfoOpen(false)}
          onAvatarChanged={onChatsChanged}
          onLeft={() => {
            setChatInfoOpen(false);
            onChatsChanged();
            onBack();
          }}
          onOpenLightbox={openLightbox}
          lightboxOpen={!!lightbox}
          onPreviewPinned={setPinnedPreview}
          onOpenChat={(id) => {
            setChatInfoOpen(false);
            onOpenChat(id);
          }}
          pinnedShared={pinnedShared}
          pinnedPersonal={pinnedPersonal}
          onTogglePin={togglePin}
          onJumpToMessage={(id) => {
            // Контекстное меню файлов в инфопанели → "Перейти к сообщению" (см. отчёт
            // пользователя). Панель закрываем — иначе она перекрывает подсвеченное сообщение.
            setChatInfoOpen(false);
            goToMessage(id);
          }}
        />
        </ErrorBoundary>
      )}

      {pinnedPreview && (
        <div className="modal-backdrop pinned-preview-backdrop" {...backdropClose(() => setPinnedPreview(null))}>
          <div className="modal pinned-preview" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="modal-close" onClick={() => setPinnedPreview(null)}>✕</button>
            <div className="pinned-preview-header">
              <Avatar userId={pinnedPreview.senderId} name={pinnedPreview.senderName || ''} size={36} />
              <div>
                <div className="pinned-preview-name">
                  {pinnedPreview.senderId === me?.id ? t('reply.self') : pinnedPreview.senderName || t('reply.senderFallback')}
                </div>
                <div className="pinned-preview-time">
                  {new Date(pinnedPreview.createdAt).toLocaleString(intlLocale(i18n.resolvedLanguage), {
                    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                  })}
                </div>
              </div>
            </div>
            <div className="pinned-preview-body">
              {renderMedia(pinnedPreview, openLightbox, pinnedPreview.senderId === me?.id, transcripts.get(pinnedPreview.id))}
            </div>
            <div className="composer pinned-reply-composer">
              <div className="ta-wrap">
                <textarea
                  ref={pinnedReplyTaRef}
                  placeholder={t('messagePlaceholder')}
                  rows={1}
                  value={pinnedReplyText}
                  disabled={pinnedReplySending}
                  onChange={(e) => setPinnedReplyText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendPinnedReply();
                    }
                  }}
                />
                <button
                  type="button"
                  className="emoji-toggle-btn"
                  title={t('emoji')}
                  disabled={pinnedReplySending}
                  onClick={() => setPinnedReplyEmojiOpen((v) => !v)}
                >
                  <img src="/emoji.svg" alt={t('emoji')} />
                </button>
                {pinnedReplyEmojiOpen && (
                  <Suspense fallback={null}>
                    <EmojiPicker onBackspace={() => setPinnedReplyText(backspaceInput(pinnedReplyText, pinnedReplyTaRef.current))} onSelect={insertPinnedReplyEmoji} onClose={() => setPinnedReplyEmojiOpen(false)} />
                  </Suspense>
                )}
              </div>
              <button
                type="button"
                className={'send-btn' + (!pinnedReplyText.trim() ? ' empty' : '')}
                disabled={pinnedReplySending || !pinnedReplyText.trim()}
                title={t('send')}
                onClick={sendPinnedReply}
              >
                <img src="/send.svg" alt={t('send')} />
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDeleteId != null && (
        <ConfirmModal
          message={t('menu.deleteMessageConfirm')}
          onConfirm={async () => {
            const id = confirmDeleteId;
            if (id == null) return;
            try {
              await onDeleteMessage(chatId, id);
            } catch (error) {
              showToast(error instanceof ApiError ? error.message : t('deleteFailed'));
            } finally {
              setConfirmDeleteId(null);
            }
          }}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}

      {ctxMenu && (
        <>
          <div
            className="ctx-backdrop"
            onClick={() => setCtxMenu(null)}
            onContextMenu={(e) => {
              e.preventDefault();
              setCtxMenu(null);
            }}
          />
          <div ref={ctxMenuRef} className="ctx-menu" style={{ left: ctxMenu.x, top: ctxMenu.y }}>
            <div className="ctx-reactions">
              {QUICK_REACTIONS.map((e) => (
                <button
                  key={e}
                  type="button"
                  className="ctx-reaction-btn"
                  onClick={() => {
                    reactToMessage(ctxMenu.id, e);
                    setCtxMenu(null);
                  }}
                >
                  <Emoji char={e} />
                </button>
              ))}
              <div className="ctx-reaction-more-wrap">
                <button
                  type="button"
                  className="ctx-reaction-btn ctx-reaction-more-btn"
                  title={t('menu.moreReactions')}
                  onClick={() => setReactionPickerOpen((v) => !v)}
                >
                  <img src="/plus.svg" alt={t('menu.moreReactions')} />
                </button>
              </div>
            </div>
            {!!copyableMessageText(messages.find((message) => message.id === ctxMenu.id)) && (
              <button
                className="ctx-item"
                onClick={() => {
                  const message = messages.find((item) => item.id === ctxMenu.id);
                  copyText(ctxMenu.selectionText || copyableMessageText(message));
                  setCtxMenu(null);
                }}
              >
                <img src="/document.svg" alt="" />
                {ctxMenu.selectionText ? t('menu.copySelected') : t('menu.copy')}
              </button>
            )}
            {(() => {
              const message = messages.find((item) => item.id === ctxMenu.id);
              const { all, current } = messageDownloadItems(message, ctxMenu.fileId);
              if (!current) return null;
              return (
                <>
                  <button
                    className="ctx-item"
                    disabled={!!albumDl && !albumDl.finished}
                    onClick={() => {
                      setCtxMenu(null);
                      void downloadAlbumItems([current], false);
                    }}
                  >
                    <img src="/download-accent.svg" alt="" /> {t(all.length > 1 ? 'lightbox.downloadThis' : 'menu.download')}
                  </button>
                  {all.length > 1 && (
                    <button
                      className="ctx-item"
                      disabled={!!albumDl && !albumDl.finished}
                      onClick={() => {
                        setCtxMenu(null);
                        void downloadAlbumItems(all, false);
                      }}
                    >
                      <img src="/download-accent.svg" alt="" /> {t('lightbox.downloadAll')}
                    </button>
                  )}
                </>
              );
            })()}
            {(() => {
              const msg = messages.find((m) => m.id === ctxMenu.id);
              const canTranscribe = msg && transcribeFileId(msg);
              const st = transcripts.get(ctxMenu.id)?.status;
              if (!canTranscribe || st === 'done' || st === 'busy') return null;
              return (
                <button
                  className="ctx-item"
                  onClick={() => {
                    runTranscribe(ctxMenu.id);
                    setCtxMenu(null);
                  }}
                >
                  <img src="/transcribe.svg" alt="" /> {t('transcribe.action')}
                </button>
              );
            })()}
            {isGroup && !ctxMenu.mine && (
              <button
                className="ctx-item"
                onClick={() => {
                  const msg = messages.find((m) => m.id === ctxMenu.id);
                  if (msg) setInfoUserId(msg.senderId);
                  setCtxMenu(null);
                }}
              >
                <img src="/profile.svg" alt="" /> {t('menu.userInfo')}
              </button>
            )}
            {(() => {
              const msg = messages.find((m) => m.id === ctxMenu.id);
              // «Информация» — время доставки/прочтения; только у своих уже отправленных сообщений.
              if (!msg || msg.senderId !== me?.id || msg.id <= 0) return null;
              return (
                <button
                  className="ctx-item"
                  onClick={() => {
                    setInfoMsgId(msg.id);
                    setCtxMenu(null);
                  }}
                >
                  <img src="/help-circle.svg" alt="" /> {t('menu.info')}
                </button>
              );
            })()}
            {(() => {
              const msg = messages.find((m) => m.id === ctxMenu.id);
              if (!msg?.reactions?.length) return null;
              return (
                <button
                  className="ctx-item"
                  onClick={() => {
                    setReactionListMsg(msg);
                    setCtxMenu(null);
                  }}
                >
                  <Emoji char={msg.reactions[0].emoji} /> {t('menu.reactions')}
                </button>
              );
            })()}
            <button
              className="ctx-item"
              onClick={() => {
                handleReply(ctxMenu.id);
                setCtxMenu(null);
              }}
            >
              <img src="/reply.svg" alt="" /> {t('menu.reply')}
            </button>
            <button
              className="ctx-item"
              onClick={() => {
                setForwardIds([ctxMenu.id]);
                setCtxMenu(null);
              }}
            >
              <img src="/forward.svg" alt="" /> {t('menu.forward')}
            </button>
            {(() => {
              const isPinnedShared = Boolean(messages.find((m) => m.id === ctxMenu.id)?.pinnedAt);
              const isPinnedPersonal = pinnedPersonal.some((m) => m.id === ctxMenu.id);
              const isPinned = isPinnedShared || isPinnedPersonal;
              return (
                <button
                  className="ctx-item"
                  onClick={() => {
                    if (isPinned) {
                      // Откреплять сразу, без диалога (как в Telegram) — диалог только на закреплении.
                      if (isPinnedShared) togglePinShared(ctxMenu.id, false);
                      if (isPinnedPersonal) togglePinPersonal(ctxMenu.id, false);
                    } else {
                      setPinConfirmId(ctxMenu.id);
                    }
                    setCtxMenu(null);
                  }}
                >
                  <img src="/pin.svg" alt="" /> {isPinned ? t('menu.unpin') : t('menu.pin')}
                </button>
              );
            })()}
            {ctxMenu.mine && (
              <button
                className="ctx-item"
                onClick={() => {
                  handleEdit(ctxMenu.id);
                  setCtxMenu(null);
                }}
              >
                <img src="/edit.svg" alt="" /> {t('menu.edit')}
              </button>
            )}
            {messages.some((message) => message.id === ctxMenu.id && canDeleteMessage(message.senderId)) && (
              <button
                className="ctx-item danger"
                onClick={() => {
                  handleDelete(ctxMenu.id);
                  setCtxMenu(null);
                }}
              >
                <img src="/trash.svg" alt="" /> {t('menu.delete')}
              </button>
            )}
          </div>
          {reactionPickerOpen && createPortal(
            <div className="ctx-reaction-picker-portal" style={reactionPickerStyle(ctxMenu)}>
              <Suspense fallback={null}>
                <EmojiPicker
                  onSelect={(e) => {
                    reactToMessage(ctxMenu.id, e);
                    setReactionPickerOpen(false);
                    setCtxMenu(null);
                  }}
                  onClose={() => setReactionPickerOpen(false)}
                />
              </Suspense>
            </div>,
            document.body,
          )}
        </>
      )}

      {forwardIds && (
        <ForwardModal
          chats={chats}
          count={forwardIds.length}
          onClose={() => setForwardIds(null)}
          onForward={doForward}
        />
      )}

      {pinConfirmId != null && (
        <PinConfirmModal
          isGroup={isGroup}
          otherName={chat?.title || t('contact.pinFallback')}
          onCancel={() => setPinConfirmId(null)}
          onConfirm={(pinForAll) => {
            if (pinForAll) togglePinShared(pinConfirmId, true);
            else togglePinPersonal(pinConfirmId, true);
            setPinConfirmId(null);
          }}
        />
      )}

      {infoUserId && (
        <UserInfoModal
          userId={infoUserId}
          onClose={() => setInfoUserId(null)}
          onOpenChat={(id) => {
            setInfoUserId(null);
            onOpenChat(id);
          }}
        />
      )}

      {infoMsgId != null && (
        <MessageInfoModal chatId={chatId} messageId={infoMsgId} onClose={() => setInfoMsgId(null)} />
      )}

      {reactionListMsg && (
        <ReactionListModal
          reactions={reactionListMsg.reactions ?? []}
          meId={me?.id}
          onClose={() => setReactionListMsg(null)}
        />
      )}
    </>
  );
}
